import axios from 'axios';
import axiosRetry from 'axios-retry';
import MockAdapter from 'axios-mock-adapter';
import toast from 'react-hot-toast';
import * as mockData from './mockData';
import { getBrandedNode } from './branding';
import { 
  Newsletter, 
  Referral,
  LeaderboardEntry,
  LeaderboardSubmission,
  LeaderboardSubmissionResponse,
  AdminBroadcastParams,
  SystemAlert,
  BroadcastRecipient
} from '../types';

import { SHOULD_USE_MOCKS, ENABLE_HEALTH_CHECKS } from '../lib/clerk';
import { ALL_CAPABILITIES, PUBLIC_PLATFORM_PATHS } from '../constants';

declare module 'axios' {
  export interface AxiosRequestConfig {
    silent?: boolean;
    skipErrorReporting?: boolean;
    skipToast?: boolean;
  }
}

const adminArticles: any[] = [];
const adminTickets: any[] = [
  { id: 'tkt1', organization: 'Beta Chapter', subject: 'Payout Failure', status: 'OPEN', priority: 'HIGH', submittedAt: new Date().toISOString() }
];
const mockNotifications: any[] = [
  {
    id: 'notif-welcome',
    userId: 'usr_super_admin',
    nodeId: 'platform-root',
    type: 'SYSTEM_ALERT',
    title: 'Command Center Active',
    message: 'System services and nodes are operational.',
    createdAt: new Date().toISOString(),
    read: false
  }
];


const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''; // Respect VITE_API_BASE_URL if set, otherwise use relative paths to proxy through server
const IS_DEMO_SESSION = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('is_demo_session') === 'true';
const USE_MOCK_DATA = SHOULD_USE_MOCKS || IS_DEMO_SESSION || (typeof window !== 'undefined' && !!(window as any).__PRERENDER_INJECTED?.isPrerendering);


// Paginated Response Interface matching Spring Data Page<T>
export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number; // Current page index (0-based)
  first: boolean;
  last: boolean;
  empty: boolean;
  pageable?: any;
}

export const apiWorker = axios.create({ 
  baseURL: BASE_URL || '/',
  timeout: 30000, 
});

let globalGetToken: (() => Promise<string | null>) | null = null;
export const setGlobalGetToken = (fn: (() => Promise<string | null>) | null) => {
  globalGetToken = fn;
};

// Expose apiWorker to window for admin debugging
if (typeof window !== 'undefined') {
  (window as any).apiWorker = apiWorker;
}

axiosRetry(apiWorker, {
  retries: 2, // Reduced from 3 to fail faster in the UI for persistent errors
  retryDelay: (retryCount) => {
    return Math.min(axiosRetry.exponentialDelay(retryCount), 3000); // Cap max delay at 3 seconds
  },
  retryCondition: (error) => {
    // FAST FAIL: Do not retry if we already know the backend is explicitly unreachable
    if ((error as any).isAvailabilityError) return false;

    // Avoid retrying on auth, client validation, conflict, rate limits, or not found errors
    const status = error.response?.status;
    if (status && [400, 401, 403, 404, 409, 422, 429].includes(status)) return false;
    // Retry on network errors or idempotent request errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error);
  },
  shouldResetTimeout: true,
});

// Mocking logic
if (USE_MOCK_DATA) {
  const mock = new MockAdapter(apiWorker, { delayResponse: 100, onNoMatch: 'passthrough' });

  // Helper to wrap array responses in Spring Data Page format
  const paginate = (items: any[] = [], page: number = 0, size: number = 20, totalElements?: number): PaginatedResponse<any> => {
    const safeItems = Array.isArray(items) ? items : [];
    const total = totalElements !== undefined ? totalElements : safeItems.length;
    return {
      content: safeItems,
      pageable: {
        sort: { sorted: true, unsorted: false, empty: false },
        pageNumber: page,
        pageSize: size,
        offset: page * size,
        paged: true,
        unpaged: false
      } as any,
      totalElements: total,
      totalPages: Math.max(1, Math.ceil(total / size)),
      last: (page + 1) * size >= total,
      first: page === 0,
      size: size,
      number: page,
      empty: total === 0
    };
  };

  const defaultMarketScores = [
    { playerName: 'President Avery', score: 145200, netWorth: 145200, date: '2026-07-20' },
    { playerName: 'Treasurer Mike', score: 98450, netWorth: 98450, date: '2026-07-21' },
    { playerName: 'Alex (Theta Chi)', score: 64100, netWorth: 64100, date: '2026-07-22' },
    { playerName: 'Sam (Greeks)', score: 41200, netWorth: 41200, date: '2026-07-19' },
    { playerName: 'Jordan (HOA Board)', score: 28900, netWorth: 28900, date: '2026-07-18' }
  ];

  const defaultSubmarineScores = [
    { playerName: 'Captain Avery', score: 3450, depth: '345m', date: '2026-07-22', metadata: { distance: 3450, livesRemaining: 1 } },
    { playerName: 'Submarine Sam', score: 2890, depth: '289m', date: '2026-07-21', metadata: { distance: 2890, livesRemaining: 0 } },
    { playerName: 'DeepSea Mike', score: 2100, depth: '210m', date: '2026-07-20', metadata: { distance: 2100, livesRemaining: 1 } },
    { playerName: 'Abyss Explorer', score: 1750, depth: '175m', date: '2026-07-19', metadata: { distance: 1750, livesRemaining: 0 } },
    { playerName: 'Nautilus', score: 1200, depth: '120m', date: '2026-07-18', metadata: { distance: 1200, livesRemaining: 0 } }
  ];

  const defaultNodeManScores = [
    { playerName: 'Compliance Pro', score: 14850, level: 3, date: '2026-07-23', metadata: { levelReached: 3, nodesConnected: 120 } },
    { playerName: 'Auditor Avery', score: 11200, level: 2, date: '2026-07-22', metadata: { levelReached: 2, nodesConnected: 95 } },
    { playerName: 'Treasurer Tom', score: 8400, level: 2, date: '2026-07-21', metadata: { levelReached: 2, nodesConnected: 60 } },
    { playerName: 'Board Director', score: 6250, level: 1, date: '2026-07-20', metadata: { levelReached: 1, nodesConnected: 45 } },
    { playerName: 'Org Master', score: 4100, level: 1, date: '2026-07-19', metadata: { levelReached: 1, nodesConnected: 30 } }
  ];

  const defaultOrgRunScores = [
    { playerName: 'SpeedyAdmin', score: 1450, distance: 1200, date: '2026-08-07', metadata: { distance: 1200 } },
    { playerName: 'BylawRunner', score: 980, distance: 850, date: '2026-08-07', metadata: { distance: 850 } },
    { playerName: 'Chairperson', score: 620, distance: 540, date: '2026-08-06', metadata: { distance: 540 } }
  ];

  const defaultMicroMachinesScores = [
    { playerName: 'ApexDrifter', score: 3820, track: 'boardroom', bestLap: 1240, totalTime: 3820, date: '2026-08-07', metadata: { track: 'boardroom', bestLap: 1240, totalTime: 3820 } },
    { playerName: 'DeskRacer', score: 4100, track: 'boardroom', bestLap: 1350, totalTime: 4100, date: '2026-08-07', metadata: { track: 'boardroom', bestLap: 1350, totalTime: 4100 } },
    { playerName: 'TheIntern', score: 4520, track: 'boardroom', bestLap: 1480, totalTime: 4520, date: '2026-08-06', metadata: { track: 'boardroom', bestLap: 1480, totalTime: 4520 } }
  ];

  const defaultOrgleScores = [
    { playerName: 'WordMaster', score: 600, guessesUsed: 1, date: '2026-08-10', metadata: { word: 'BOARD', guessesUsed: 1 } },
    { playerName: 'LexiconPro', score: 500, guessesUsed: 2, date: '2026-08-09', metadata: { word: 'CHAIR', guessesUsed: 2 } },
    { playerName: 'BylawSpeller', score: 400, guessesUsed: 3, date: '2026-08-08', metadata: { word: 'VOTES', guessesUsed: 3 } }
  ];

  const getLimitFromConfig = (config: any) => {
    const params = config.params || {};
    if (params.limit) return parseInt(params.limit, 10) || 10;
    if (config.url?.includes('?')) {
      const sp = new URLSearchParams(config.url.split('?')[1]);
      const l = sp.get('limit');
      if (l) return parseInt(l, 10) || 10;
    }
    return 10;
  };

  const getNodeFromConfig = (config: any): string | null => {
    const params = config.params || {};
    if (params.nodeId) return params.nodeId;
    if (config.url?.includes('?')) {
      const sp = new URLSearchParams(config.url.split('?')[1]);
      const n = sp.get('nodeId');
      if (n) return n;
    }
    return null;
  };

  mock.onGet(/(?:\/api\/tools\/org-market-wars\/high-scores|\/api\/games\/org-market-wars\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    const limit = getLimitFromConfig(config);
    const targetNodeId = getNodeFromConfig(config);
    let customList: any[] = [];
    try {
      const customSaved = localStorage.getItem('org_market_wars_scores');
      const parsed = customSaved ? JSON.parse(customSaved) : [];
      customList = Array.isArray(parsed) ? parsed : [];
    } catch {
      customList = [];
    }
    let combined = [...customList, ...defaultMarketScores].sort((a, b) => (b.netWorth || b.score) - (a.netWorth || a.score));
    if (targetNodeId) {
      combined = combined.filter((item: any) => item.nodeId === targetNodeId);
    }
    return [200, paginate(combined.slice(0, limit), 0, limit, combined.length)];
  });

  mock.onPost(/(?:\/api\/tools\/org-market-wars\/high-scores|\/api\/games\/org-market-wars\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    try {
      const body = JSON.parse(config.data || '{}');
      const val = typeof body.score === 'number' ? body.score : body.netWorth;
      if (body.playerName && typeof val === 'number') {
        let customList: any[] = [];
        try {
          const customSaved = localStorage.getItem('org_market_wars_scores');
          const parsed = customSaved ? JSON.parse(customSaved) : [];
          customList = Array.isArray(parsed) ? parsed : [];
        } catch {
          customList = [];
        }
        customList.push({ playerName: body.playerName, score: val, netWorth: val, nodeId: body.nodeId, date: new Date().toISOString().split('T')[0], metadata: body.metadata });
        localStorage.setItem('org_market_wars_scores', JSON.stringify(customList));
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  mock.onGet(/(?:\/api\/tools\/deep-sea-explorer\/high-scores|\/api\/games\/deep-sea-explorer\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    const limit = getLimitFromConfig(config);
    const targetNodeId = getNodeFromConfig(config);
    let customList: any[] = [];
    try {
      const customSaved = localStorage.getItem('deep_sea_explorer_scores');
      const parsed = customSaved ? JSON.parse(customSaved) : [];
      customList = Array.isArray(parsed) ? parsed : [];
    } catch {
      customList = [];
    }
    let combined = [...customList, ...defaultSubmarineScores].sort((a, b) => b.score - a.score);
    if (targetNodeId) {
      combined = combined.filter((item: any) => item.nodeId === targetNodeId);
    }
    return [200, paginate(combined.slice(0, limit), 0, limit, combined.length)];
  });

  mock.onPost(/(?:\/api\/tools\/deep-sea-explorer\/high-scores|\/api\/games\/deep-sea-explorer\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    try {
      const body = JSON.parse(config.data || '{}');
      if (body.playerName && typeof body.score === 'number') {
        let customList: any[] = [];
        try {
          const customSaved = localStorage.getItem('deep_sea_explorer_scores');
          const parsed = customSaved ? JSON.parse(customSaved) : [];
          customList = Array.isArray(parsed) ? parsed : [];
        } catch {
          customList = [];
        }
        customList.push({ playerName: body.playerName, score: body.score, depth: `${body.score}m`, nodeId: body.nodeId, date: new Date().toISOString().split('T')[0], metadata: body.metadata });
        localStorage.setItem('deep_sea_explorer_scores', JSON.stringify(customList));
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  mock.onPost(/\/api\/errors\/report(?:\?.*)?$/i).reply(() => {
    return [200, { success: true, ticketId: `ERR-${Date.now().toString(36).toUpperCase()}` }];
  });

  mock.onGet(/(?:\/api\/tools\/node-man\/high-scores|\/api\/games\/node-man\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    const limit = getLimitFromConfig(config);
    const targetNodeId = getNodeFromConfig(config);
    let customList: any[] = [];
    try {
      const customSaved = localStorage.getItem('node_man_scores');
      const parsed = customSaved ? JSON.parse(customSaved) : [];
      customList = Array.isArray(parsed) ? parsed : [];
    } catch {
      customList = [];
    }
    let combined = [...customList, ...defaultNodeManScores].sort((a, b) => b.score - a.score);
    if (targetNodeId) {
      combined = combined.filter((item: any) => item.nodeId === targetNodeId);
    }
    return [200, paginate(combined.slice(0, limit), 0, limit, combined.length)];
  });

  mock.onPost(/(?:\/api\/tools\/node-man\/high-scores|\/api\/games\/node-man\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    try {
      const body = JSON.parse(config.data || '{}');
      if (body.playerName && typeof body.score === 'number') {
        let customList: any[] = [];
        try {
          const customSaved = localStorage.getItem('node_man_scores');
          const parsed = customSaved ? JSON.parse(customSaved) : [];
          customList = Array.isArray(parsed) ? parsed : [];
        } catch {
          customList = [];
        }
        customList.push({ playerName: body.playerName, score: body.score, level: body.metadata?.levelReached || body.level || 1, nodeId: body.nodeId, date: new Date().toISOString().split('T')[0], metadata: body.metadata });
        localStorage.setItem('node_man_scores', JSON.stringify(customList));
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  mock.onGet(/(?:\/api\/tools\/orgrun\/high-scores|\/api\/games\/orgrun\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    const limit = getLimitFromConfig(config);
    const targetNodeId = getNodeFromConfig(config);
    let list: any[] = [];
    try {
      const saved = localStorage.getItem('orgrun_leaderboard');
      const parsed = saved ? JSON.parse(saved) : defaultOrgRunScores;
      list = Array.isArray(parsed) ? parsed : [...defaultOrgRunScores];
    } catch {
      list = [...defaultOrgRunScores];
    }
    if (targetNodeId) {
      list = list.filter((item: any) => item.nodeId === targetNodeId);
    }
    return [200, paginate(list.slice(0, limit), 0, limit, list.length)];
  });

  mock.onPost(/(?:\/api\/tools\/orgrun\/high-scores|\/api\/games\/orgrun\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    try {
      const body = JSON.parse(config.data || '{}');
      if (body.playerName) {
        let list: any[] = [];
        try {
          const saved = localStorage.getItem('orgrun_leaderboard');
          const parsed = saved ? JSON.parse(saved) : defaultOrgRunScores;
          list = Array.isArray(parsed) ? parsed : [...defaultOrgRunScores];
        } catch {
          list = [...defaultOrgRunScores];
        }
        list.push(body);
        list.sort((a: any, b: any) => b.score - a.score);
        localStorage.setItem('orgrun_leaderboard', JSON.stringify(list.slice(0, 20)));
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  mock.onGet(/(?:\/api\/tools\/micromachines\/high-scores|\/api\/games\/micromachines\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    const limit = getLimitFromConfig(config);
    const targetNodeId = getNodeFromConfig(config);
    let list: any[] = [];
    try {
      const saved = localStorage.getItem('mm_leaderboard');
      const parsed = saved ? JSON.parse(saved) : defaultMicroMachinesScores;
      list = Array.isArray(parsed) ? parsed : [...defaultMicroMachinesScores];
    } catch {
      list = [...defaultMicroMachinesScores];
    }
    list = list.map((item: any) => ({
      ...item,
      totalTime: item.totalTime ?? item.metadata?.totalTime ?? item.score,
      track: item.track ?? item.metadata?.track ?? 'boardroom',
      bestLap: item.bestLap ?? item.metadata?.bestLap,
    }));
    if (targetNodeId) {
      list = list.filter((item: any) => item.nodeId === targetNodeId);
    }
    return [200, paginate(list.slice(0, limit), 0, limit, list.length)];
  });

  mock.onPost(/(?:\/api\/tools\/micromachines\/high-scores|\/api\/games\/micromachines\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    try {
      const body = JSON.parse(config.data || '{}');
      if (body.playerName) {
        let list: any[] = [];
        try {
          const saved = localStorage.getItem('mm_leaderboard');
          const parsed = saved ? JSON.parse(saved) : defaultMicroMachinesScores;
          list = Array.isArray(parsed) ? parsed : [...defaultMicroMachinesScores];
        } catch {
          list = [...defaultMicroMachinesScores];
        }
        const totalTime = body.totalTime ?? body.metadata?.totalTime ?? body.score;
        const entryToSave = {
          playerName: body.playerName,
          score: body.score ?? totalTime,
          totalTime,
          track: body.track ?? body.metadata?.track ?? 'boardroom',
          bestLap: body.bestLap ?? body.metadata?.bestLap,
          nodeId: body.nodeId,
          date: new Date().toISOString(),
          metadata: {
            track: body.track ?? body.metadata?.track ?? 'boardroom',
            bestLap: body.bestLap ?? body.metadata?.bestLap,
            totalTime,
            ...(body.metadata || {})
          }
        };
        list.push(entryToSave);
        list.sort((a: any, b: any) => (a.totalTime || a.score) - (b.totalTime || b.score));
        localStorage.setItem('mm_leaderboard', JSON.stringify(list.slice(0, 20)));
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  mock.onGet(/(?:\/api\/tools\/orgle\/high-scores|\/api\/games\/orgle\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    const limit = getLimitFromConfig(config);
    const targetNodeId = getNodeFromConfig(config);
    let list: any[] = [];
    try {
      const saved = localStorage.getItem('orgle_leaderboard');
      const parsed = saved ? JSON.parse(saved) : defaultOrgleScores;
      list = Array.isArray(parsed) ? parsed : [...defaultOrgleScores];
    } catch {
      list = [...defaultOrgleScores];
    }
    if (targetNodeId) {
      list = list.filter((item: any) => item.nodeId === targetNodeId);
    }
    return [200, paginate(list.slice(0, limit), 0, limit, list.length)];
  });

  mock.onPost(/(?:\/api\/tools\/orgle\/high-scores|\/api\/games\/orgle\/leaderboard)(?:\?.*)?$/i).reply((config) => {
    try {
      const body = JSON.parse(config.data || '{}');
      if (body.playerName) {
        let list: any[] = [];
        try {
          const saved = localStorage.getItem('orgle_leaderboard');
          const parsed = saved ? JSON.parse(saved) : defaultOrgleScores;
          list = Array.isArray(parsed) ? parsed : [...defaultOrgleScores];
        } catch {
          list = [...defaultOrgleScores];
        }
        list.push(body);
        list.sort((a: any, b: any) => (b.score || 0) - (a.score || 0));
        localStorage.setItem('orgle_leaderboard', JSON.stringify(list.slice(0, 20)));
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  // Fallback route for POST /api/scores
  mock.onPost(/\/api\/scores(?:\?.*)?$/i).reply((config) => {
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  // Generic fallback for any other games leaderboard
  mock.onGet(/\/api\/(?:games|tools)\/[^/]+\/(?:leaderboard|high-scores)(?:\?.*)?$/i).reply((config) => {
    const limit = getLimitFromConfig(config);
    return [200, paginate([], 0, limit, 0)];
  });

  mock.onPost(/\/api\/errors\/report(?:\?.*)?$/i).reply((config) => {
    const ticketId = 'ERR-' + Math.random().toString(36).substring(2, 10).toUpperCase();
    try {
      const payload = JSON.parse(config.data || '{}');
      console.log('[Mock Error Reporter] Received crash report:', { ticketId, payload });
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [200, { success: true, ticketId, message: 'Crash report received' }];
  });

  // Default identities mapping
  const getMockUserId = () => {
    const saved = localStorage.getItem('mock_user_identity');
    if (saved) {
      try {
        return JSON.parse(saved).id;
      } catch (e) { return 'user-1'; }
    }
    return 'user-1';
  };

  // Persistence for created hubs in the mock environment
  const getCreatedNodes = (): any[] => {
    try {
      const saved = localStorage.getItem('mock_created_nodes');
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  };

  const saveCreatedNode = (node: any) => {
    const current = getCreatedNodes();
    const index = current.findIndex(n => n.id === node.id);
    if (index > -1) {
      current[index] = node;
      localStorage.setItem('mock_created_nodes', JSON.stringify(current));
    } else {
      localStorage.setItem('mock_created_nodes', JSON.stringify([...current, node]));
    }
  };

  // Nodes & Memberships
  const membershipHandler = (): [number, any] => {
    const userId = getMockUserId();
    const memberships = [...(mockData.MOCK_MEMBERSHIPS_MAP[userId] || [])];
    
    // Add created hubs to memberships if not present
    const created = getCreatedNodes();
    created.forEach(node => {
      if (!memberships.find(m => m.id === node.id)) {
        memberships.push({
          id: node.id,
          nodeName: node.name,
          nodeSlug: node.slug,
          role: 'OWNER',
          unreadCount: 0,
          tier: node.tier || 'LOCAL'
        });
      }
    });

    // Ensure all memberships have a tier, metrics, and pre-resolved capabilities for UI testing
    const enrichedMemberships = memberships.map(m => {
      const roleUpper = m.role.toUpperCase();
      let caps = [];
      if (['SUPER_ADMIN', 'SYSTEM_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN', 'OWNER', 'ADMIN', 'PRESIDENT', 'FOUNDER', 'MANAGER', 'DIRECTOR', 'MISSION_COMMANDER'].includes(roleUpper)) {
        caps = [...ALL_CAPABILITIES];
      } else if (roleUpper === 'TREASURER') {
        caps = ['view_ledger', 'configure_fees', 'view_ledger_admin', 'view_personal_finances', 'view_newsletter', 'view_chat', 'view_calendar', 'view_requirements', 'view_goals', 'view_discussions', 'view_polls', 'view_directory', 'view_elections', 'view_leadership', 'view_support'];
      } else if (roleUpper === 'CAPTAIN' || roleUpper === 'COORDINATOR') {
        caps = ['create_announcement', 'create_event', 'cast_vote', 'view_newsletter', 'view_chat', 'view_calendar', 'view_requirements', 'view_goals', 'view_discussions', 'view_polls', 'view_directory', 'view_elections', 'view_leadership', 'view_support'];
      } else {
        caps = ['cast_vote', 'view_newsletter', 'view_chat', 'view_calendar', 'view_requirements', 'view_goals', 'view_discussions', 'view_polls', 'view_directory', 'view_elections', 'view_leadership', 'view_personal_finances', 'view_support'];
      }

      return {
        ...m,
        nodeId: m.id,
        capabilities: caps,
        tier: m.tier || 'LOCAL',
        storageUsedBytes: m.storageUsedBytes || (m.tier === 'LOCAL' ? 45 * 1024 * 1024 : m.tier === 'FOUNDATION' ? 500 * 1024 * 1024 : 1500 * 1024 * 1024), // Mock partial usage
        memberCount: m.memberCount || (m.tier === 'LOCAL' ? 12 : m.tier === 'FOUNDATION' ? 85 : 45)
      };
    });

    return [200, enrichedMemberships];
  };

  mock.onGet(/\/api\/nodes\/me(?:\?.*)?$/).reply(membershipHandler);
  mock.onGet(/\/api\/users\/me\/memberships(?:\?.*)?$/).reply(membershipHandler);

  // Check node name availability
  mock.onGet(/\/api\/nodes\/check-name(?:\?.*)?$/i).reply((config): [number, any] => {
    let name = '';
    try {
      if (config.params && config.params.name) {
        name = String(config.params.name);
      } else if (config.url) {
        const parsed = new URL(config.url, 'http://localhost');
        name = parsed.searchParams.get('name') || '';
      }
    } catch {
      name = '';
    }
    
    if (!name.trim()) {
      return [200, { name, available: false }];
    }
    
    const cleanName = name.trim().toLowerCase();
    const created = getCreatedNodes();
    const existsInCreated = created.some(n => 
      (n.name && n.name.toLowerCase() === cleanName) || 
      (n.title && n.title.toLowerCase() === cleanName)
    );
    const existsInMock = (mockData.NODES || []).some((n: any) => 
      (n.name && n.name.toLowerCase() === cleanName) || 
      (n.title && n.title.toLowerCase() === cleanName)
    );
    
    return [200, { name, available: !existsInCreated && !existsInMock }];
  });

  // Node details
  mock.onGet(/\/api\/nodes\/by-slug\/[^/]+(?:\?.*)?$/).reply((config): [number, any] => {
    const url = config.url || '';
    const segments = url.split('?')[0].split('/').filter(Boolean);
    const slug = segments.pop();
    
    const created = getCreatedNodes();
    const found = created.find(n => n.slug === slug);
    if (found) return [200, found];

    const params = config.params || {};
    return [200, getBrandedNode(params.domain, slug)];
  });

  mock.onGet(/\/api\/nodes\/[^/]+(?:\?.*)?$/).reply((config): [number, any] => {
    const url = config.url || '';
    const segments = url.split('?')[0].split('/').filter(Boolean);
    const nodeId = segments.pop();
    
    // Explicitly handle 'me' if it reached here
    if (nodeId === 'me') {
      return membershipHandler();
    }

    const created = getCreatedNodes();
    const found = created.find(n => n.id === nodeId);
    if (found) return [200, found];
    
    const params = config.params || {};
    return [200, getBrandedNode(params.domain, nodeId)];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/roles(?:\?.*)?$/).reply(200, []);

  mock.onGet(/\/api\/nodes\/[^/]+\/lineage(?:\?.*)?$/).reply((config) => {
    // Return breadcrumb list
    return [200, [mockData.NODES[0]]];
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/tree(?:\?.*)?$/).reply((config) => {
    const segments = config.url?.split('?')[0].split('/') || [];
    const nodeId = segments[3];
    return [200, mockData.NODES.filter(n => n.parentId === nodeId)];
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/members\/directory(?:\?.*)?$/).reply((config) => {
    return [200, paginate(mockData.MOCK_DIRECTORY)];
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/powerups(?:\?.*)?$/).reply((config) => {
    return [200, { success: true }];
  });

  mock.onPost(/\/api\/scores(?:\?.*)?$/).reply((config) => {
    try {
      const data = JSON.parse(config.data || '{}');
      console.log('[Mock] Saved high score via /api/scores:', data);
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [201, { status: 'success', id: `score-${Date.now()}`, isNewHighScore: true }];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/full-tree(?:\?.*)?$/).reply((config) => {
    const segments = config.url?.split('?')[0].split('/') || [];
    const nodeId = segments[3];
    
    const buildTree = (id: string): any => {
      const node = mockData.NODES.find(n => n.id === id);
      if (!node) return null;
      return {
        ...node,
        children: mockData.NODES.filter(n => n.parentId === id).map(c => buildTree(c.id))
      };
    };

    return [200, buildTree(nodeId)];
  });
  // Chat & Messaging
  mock.onGet(/\/api\/nodes\/[^/]+\/channels$/).reply(200, mockData.MOCK_CHANNELS);
  mock.onGet(/\/api\/channels\/[^/]+\/messages$/).reply((config) => {
    const channelId = config.url?.split('/').slice(-2, -1)[0];
    const messages = mockData.MOCK_MESSAGES.filter(m => m.channelId === channelId);
    return [200, messages];
  });
  mock.onPost(/\/api\/channels\/[^/]+\/messages$/).reply((config) => {
    const channelId = config.url?.split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data);
    const newMessage = {
      id: `msg-${Date.now()}`,
      channelId: channelId || '',
      senderUserId: 'user-1', // Mock current user
      senderName: 'Rich Treharne',
      senderAvatarColor: 'bg-blue-500',
      body: data.body,
      createdAt: new Date().toISOString(),
      isPinned: false
    };
    mockData.MOCK_MESSAGES.push(newMessage);
    return [201, newMessage];
  });
  mock.onPatch(/\/api\/messages\/[^/]+$/).reply((config) => {
    const messageId = config.url?.split('/').pop();
    const data = JSON.parse(config.data);
    const message = mockData.MOCK_MESSAGES.find(m => m.id === messageId);
    if (message) {
      Object.assign(message, { ...data, editedAt: new Date().toISOString() });
      return [200, message];
    }
    return [404];
  });

  // Discussions & Topics (Standardized)
  const getTopics = (nodeId: string) => {
    return mockData.MOCK_TOPICS.filter(t => t.nodeId === nodeId || t.cascadesDown);
  };

  mock.onGet(/\/api\/nodes\/[^/]+\/topics(?:\?.*)?$/).reply((config) => {
    const rawPath = config.url?.split('?')[0] || '';
    const nodeId = rawPath.split('/').slice(-2, -1)[0] || '60c72b2f9b1d8b2a4c8e4d01';
    const hasPagination = config.url?.includes('page=') || config.url?.includes('size=');
    return [200, hasPagination ? paginate(getTopics(nodeId)) : getTopics(nodeId)];
  });

  // Discussions & Posts (Alias for the Pagination Spec)
  mock.onGet(/\/api\/discussions\/[^/]+\/posts(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0] || '60c72b2f9b1d8b2a4c8e4d01';
    return [200, paginate(getTopics(nodeId))];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/posts(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0] || '60c72b2f9b1d8b2a4c8e4d01';
    return [200, paginate(getTopics(nodeId))];
  });
  
  mock.onGet(/\/api\/(?:topics|posts)\/[^/]+(?:\?.*)?$/).reply((config) => {
    const topicId = config.url?.split('/').pop()?.split('?')[0];
    if (config.url?.endsWith('/comments')) return [404]; // Pass to comments handler
    const topic = mockData.MOCK_TOPICS.find(t => t.id === topicId);
    return topic ? [200, topic] : [404];
  });

  mock.onGet(/\/api\/(?:topics|posts)\/[^/]+\/comments(?:\?.*)?$/).reply((config) => {
    const parts = config.url?.split('/') || [];
    const topicId = parts[parts.length - 2];
    const rawComments = (topicId && mockData.MOCK_COMMENTS[topicId]) || [];
    
    // Nest the comments for the frontend
    const buildTree = (list: any[], parentId?: string): any[] => {
      return list
        .filter(c => c.parentId === parentId)
        .map(c => ({ 
          ...c, 
          replies: buildTree(list, c.id) 
        }));
    };
    
    return [200, buildTree(rawComments)];
  });

  mock.onPost(/\/api\/nodes\/[^/]+\/topics(?:\?.*)?$/).reply((config) => {
    const rawPath = config.url?.split('?')[0] || '';
    const nodeId = rawPath.split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data);
    const newTopic = {
      ...data,
      id: `topic-${Date.now()}`,
      nodeId: nodeId || '60c72b2f9b1d8b2a4c8e4d01',
      nodeName: 'Org HQ',
      authorUserId: 'user-1',
      authorName: 'Rich Treharne',
      commentCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    mockData.MOCK_TOPICS.unshift(newTopic);
    mockData.MOCK_COMMENTS[newTopic.id] = [];
    return [201, newTopic];
  });
  mock.onGet(/\/api\/topics\/[^/]+\/comments(?:\?.*)?$/).reply((config) => {
    const rawPath = config.url?.split('?')[0] || '';
    const topicId = rawPath.split('/').slice(-2, -1)[0];
    const comments = (topicId && mockData.MOCK_COMMENTS[topicId]) || [];
    // Nest the comments for the frontend (simplified)
    const buildTree = (list: any[], parentId?: string): any[] => {
      return list
        .filter(c => c.parentId === parentId)
        .map(c => ({ ...c, replies: buildTree(list, c.id) }));
    };
    return [200, buildTree(comments)];
  });
  mock.onPost(/\/api\/topics\/[^/]+\/comments(?:\?.*)?$/).reply((config) => {
    const rawPath = config.url?.split('?')[0] || '';
    const topicId = rawPath.split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data);
    const newComment = {
      id: `comment-${Date.now()}`,
      topicId,
      parentId: data.parentId,
      content: data.content,
      authorUserId: 'user-1',
      authorName: 'Rich Treharne',
      depth: data.depth || 0,
      createdAt: new Date().toISOString()
    };
    if (topicId) {
      mockData.MOCK_COMMENTS[topicId] = mockData.MOCK_COMMENTS[topicId] || [];
      mockData.MOCK_COMMENTS[topicId].push(newComment as any);
    }
    
    // Update topic comment count
    const topic = mockData.MOCK_TOPICS.find(t => t.id === topicId);
    if (topic) topic.commentCount = (topic.commentCount || 0) + 1;
    
    return [201, newComment];
  });

  // Referrals
  mock.onGet(/\/api\/nodes\/[^/]+\/referrals$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const referrals = mockData.MOCK_REFERRALS.filter(r => r.referrerNodeId === nodeId);
    return [200, referrals];
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/referrals$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const newReferral = {
      id: `ref-${Date.now()}`,
      referrerNodeId: nodeId || '',
      referralCode: `REF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      creditsEarned: 0,
      status: 'PENDING' as const,
      createdAt: new Date().toISOString()
    };
    mockData.MOCK_REFERRALS.unshift(newReferral);
    return [201, newReferral];
  });

  mock.onPost(/\/api\/nodes\/[^/]+\/referrals\/[^/]+\/send$/).reply((config) => {
    // Mock sending referral emails
    return [204];
  });
  mock.onGet(/\/api\/referrals\/validate\/[^/]+$/).reply((config) => {
    const code = config.url?.split('/').pop();
    const referral = mockData.MOCK_REFERRALS.find(r => r.referralCode === code);
    if (referral) {
      return [200, referral];
    }
    return [404, { message: 'Invalid referral code' }];
  });

  // Pulse & Communications
  mock.onGet(/\/api\/nodes\/[^/]+\/pulse(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('?')[0].split('/').slice(-2, -1)[0];
    const alerts = mockData.MOCK_ANNOUNCEMENTS.filter(a => a.nodeId === nodeId);
    return [200, alerts];
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/pulse\/active(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('?')[0].split('/').slice(-3, -2)[0];
    const alerts = mockData.MOCK_ANNOUNCEMENTS.filter(a => a.nodeId === nodeId && !a.resolved);
    return [200, alerts];
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/pulse(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('?')[0].split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data);
    const newAlert = {
      ...data,
      id: `alert-${Date.now()}`,
      nodeId,
      createdAt: new Date().toISOString(),
      acknowledgedBy: [],
      relayCount: 0,
      authorUserId: 'user-1',
      authorName: 'James Avery',
    };
    mockData.MOCK_ANNOUNCEMENTS.unshift(newAlert);
    return [201, newAlert];
  });
  mock.onPost(/\/api\/(?:nodes\/[^/]+\/)?pulse\/[^/]+\/acknowledge(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('?')[0].split('/').slice(-2, -1)[0];
    const alert = mockData.MOCK_ANNOUNCEMENTS.find(a => a.id === id);
    if (alert) {
      if (!alert.acknowledgedBy) alert.acknowledgedBy = [];
      if (!alert.acknowledgedBy.includes('user-1')) {
        alert.acknowledgedBy.push('user-1');
      }
      return [200, alert];
    }
    return [404];
  });
  mock.onPost(/\/api\/(?:nodes\/[^/]+\/)?pulse\/[^/]+\/relay(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('?')[0].split('/').slice(-2, -1)[0];
    const alert = mockData.MOCK_ANNOUNCEMENTS.find(a => a.id === id);
    if (alert) {
      if (!alert.relayCount) alert.relayCount = 0;
      alert.relayCount++;
      return [200, alert];
    }
    return [404];
  });
  mock.onPost(/\/api\/(?:nodes\/[^/]+\/)?pulse\/[^/]+\/view(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('?')[0].split('/').slice(-2, -1)[0];
    const alert = mockData.MOCK_ANNOUNCEMENTS.find(a => a.id === id);
    if (alert) {
      if (!alert.viewCount) alert.viewCount = 0;
      alert.viewCount++;
      return [200, alert];
    }
    return [200, { success: true }]; // Return success even if not found to prevent 401/404 noise
  });
  mock.onPost(/\/api\/(?:nodes\/[^/]+\/)?pulse\/[^/]+\/resolve(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('?')[0].split('/').slice(-2, -1)[0];
    const alert = mockData.MOCK_ANNOUNCEMENTS.find(a => a.id === id);
    if (alert) {
      alert.resolved = true;
      return [200, alert];
    }
    return [404];
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/analytics\/summary(?:\?.*)?$/).reply(200, mockData.MOCK_ANALYTICS_SUMMARY);
  mock.onGet(/\/api\/nodes\/[^/]+\/analytics\/timeseries(?:\?.*)?$/).reply(200, mockData.MOCK_ANALYTICS_TIMESERIES);
  mock.onPost(/\/api\/pulse\/[^/]+\/newsletters\/subscribe(?:\?.*)?$/).reply(200, { success: true });
  // Node configuration and visibility
  mock.onPatch(/\/api\/nodes\/[^/]+\/modules$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data);
    const node = getCreatedNodes().find(n => n.id === nodeId) || mockData.NODES.find(n => n.id === nodeId);
    if (node) {
      const currentModules = Array.isArray(node.modules) ? [...node.modules] : [];
      Object.entries(data).forEach(([moduleId, isEnabled]) => {
        if (isEnabled && !currentModules.includes(moduleId)) {
          currentModules.push(moduleId);
        } else if (!isEnabled) {
          const index = currentModules.indexOf(moduleId);
          if (index > -1) {
            currentModules.splice(index, 1);
          }
        }
      });
      node.modules = currentModules;
      node.updatedAt = new Date().toISOString();
      saveCreatedNode(node);
      return [200, node];
    }
    return [404];
  });

  mock.onPatch(/\/api\/nodes\/[^/]+\/config$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data);
    const node = getCreatedNodes().find(n => n.id === nodeId) || mockData.NODES.find(n => n.id === nodeId);
    if (node) {
      node.config = { ...(node.config || {}), ...data } as any;
      node.updatedAt = new Date().toISOString();
      saveCreatedNode(node);
      return [200, node];
    }
    return [404];
  });

  mock.onPatch(/\/api\/nodes\/[^/]+\/visibility$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const { visibility } = JSON.parse(config.data);
    const node = getCreatedNodes().find(n => n.id === nodeId) || mockData.NODES.find(n => n.id === nodeId);
    if (node) {
      node.visibility = visibility;
      node.updatedAt = new Date().toISOString();
      saveCreatedNode(node);
      return [200, node];
    }
    return [404];
  });

  mock.onPost(/\/api\/nodes\/[^/]+\/children$/).reply((config) => {
    const parentId = config.url?.split('/').slice(-2, -1)[0];
    const parent = getCreatedNodes().find(n => n.id === parentId) || mockData.NODES.find(n => n.id === parentId);
    
    if (!parent) return [404];
    
    // Tier check (Simulated)
    const currentDepth = (parent.lineageIds?.length || 0) + 1;
    const tierLimits = { LOCAL: 1, FOUNDATION: 1, MOMENTUM: 2, INSTITUTIONAL: 3 };
    if (currentDepth >= tierLimits[parent.tier as keyof typeof tierLimits]) {
      return [400, { message: 'Maximum hierarchy depth reached for your tier.' }];
    }

    const data = JSON.parse(config.data);
    const newNode = {
      ...data,
      id: '6' + Date.now().toString(16) + '00000000000',
      parentId: parent.id,
      lineageIds: [...(parent.lineageIds || []), parent.id],
      tier: parent.tier,
      visibility: 'PRIVATE',
      config: { ...(parent.config || {}), terminology: { ...(parent.config?.terminology || {}) } } as any,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    saveCreatedNode(newNode);
    return [201, newNode];
  });

  mock.onPatch(/\/api\/nodes\/[^/]+\/roles$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data); // Expecting RoleDefinition[]
    const node = getCreatedNodes().find(n => n.id === nodeId) || mockData.NODES.find(n => n.id === nodeId);
    if (node) {
      if (!node.config) node.config = {} as any;
      node.config.roleDefinitions = data;
      return [200, node.config.roleDefinitions];
    }
    return [404];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/members\/leadership$/).reply((config: any) => {
    const nodeId = config.url?.split('/').slice(-3, -2)[0];
    const node = getCreatedNodes().find(n => n.id === nodeId) || mockData.NODES.find(n => n.id === nodeId);
    const leadershipRoleNames = new Set<string>(['PRESIDENT', 'OWNER', 'ADMIN', 'TREASURER', 'SECRETARY', 'OFFICER', 'LEADER', 'SCOUTMASTER', 'CHAPTER_PRESIDENT']);
    
    // Dynamically include any roles with leadership-tier capabilities from node config
    if (node?.config?.roleDefinitions) {
      node.config.roleDefinitions.forEach((r: any) => {
        if (r.capabilities?.some((c: any) => ['manage_members', 'define_roles', 'configure_fees', 'view_ledger_admin'].includes(c))) {
          leadershipRoleNames.add(r.name);
        }
      });
    }

    const leaders = mockData.MOCK_DIRECTORY.filter(m => 
      leadershipRoleNames.has(m.role) || m.role?.toUpperCase().includes('ADMIN') || m.role?.toUpperCase().includes('PRESIDENT')
    );
    return [200, leaders];
  });

  // Members Management
  mock.onGet(/\/api\/nodes\/[^/]+\/members\/[^/]+$/).reply((config) => {
    const segments = config.url?.split('/') || [];
    const memberId = segments.pop();
    const member = mockData.MOCK_DIRECTORY.find(m => m.id === memberId);
    if (member) {
      // Simulate relationship hydration
      const relationships = mockData.MOCK_RELATIONSHIPS.filter(r => 
        r.sponsorId === member.id || r.dependentId === member.id
      );
      return [200, { ...member, relationships }];
    }
    return [404];
  });

  mock.onPatch(/\/api\/nodes\/[^/]+\/members\/[^/]+$/).reply((config) => {
    const segments = config.url?.split('/') || [];
    const memberId = segments.pop();
    const data = JSON.parse(config.data);
    const member = mockData.MOCK_DIRECTORY.find(m => m.id === memberId);
    if (member) {
      Object.assign(member, data);
      return [200, member];
    }
    return [404];
  });

  mock.onDelete(/\/api\/nodes\/[^/]+\/members\/[^/]+$/).reply((config) => {
    const segments = config.url?.split('/') || [];
    const memberId = segments.pop();
    const index = mockData.MOCK_DIRECTORY.findIndex(m => m.id === memberId || m.userId === memberId);
    if (index !== -1) {
      const deleted = mockData.MOCK_DIRECTORY.splice(index, 1)[0];
      return [200, deleted];
    }
    return [200, { success: true }];
  });

  mock.onPost(/(?:\/api\/nodes\/[^/]+\/invites|\/api\/nodes\/[^/]+\/members\/invite)(?:\?.*)?$/i).reply((config) => {
    const parts = config.url?.split('?')[0].split('/') || [];
    const isMembers = parts[parts.length - 2] === 'members';
    const nodeId = isMembers ? parts[parts.length - 3] : parts[parts.length - 2];
    const node = getCreatedNodes().find(n => n.id === nodeId) || mockData.NODES.find(n => n.id === nodeId);
    
    if (node && node.memberCount && node.memberCount >= (node.tier === 'LOCAL' ? 25 : 1000)) {
      return [400, { message: 'Maximum member limit reached for your tier.' }];
    }

    let parsed: any = {};
    try { parsed = JSON.parse(config.data || '{}'); } catch { /* ignore malformed mock payload */ }
    const email = parsed.email || 'member@example.com';
    const role = parsed.role || 'MEMBER';
    const token = Math.random().toString(36).substring(2, 10);
    const inviteLink = `https://orgsets.com/join?token=${token}`;

    const newInvite = {
      id: `inv-${Date.now()}`,
      nodeId: nodeId || 'node-1',
      nodeName: node?.name || 'Organization',
      email,
      role,
      status: 'PENDING',
      token,
      inviteLink,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 86400000).toISOString()
    };

    return [200, newInvite];
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/dashboard(?:\?.*)?$/).reply(200, {
    recentPosts: [],
    upcomingEvents: [],
    activeElections: [],
    alerts: []
  });

  // Discovery / Search
  mock.onGet(/\/api\/discovery\/search(?:\?.*)?$/).reply((config) => {
    const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
    const query = (urlParams.get('query') || config.params?.query || '').toLowerCase();
    const created = getCreatedNodes();
    let allNodes = [...created, ...mockData.NODES];
    
    if (query) {
      allNodes = allNodes.filter(n => n.name.toLowerCase().includes(query) || n.slug.toLowerCase().includes(query));
    }
    return [200, paginate(allNodes)];
  });

  mock.onGet(/\/api\/discovery\/suggest(?:\?.*)?$/).reply((config) => {
    const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
    const query = (urlParams.get('query') || config.params?.query || '').toLowerCase();
    const created = getCreatedNodes();
    let allNodes = [...created, ...mockData.NODES];
    
    if (query) {
      allNodes = allNodes.filter(n => n.name.toLowerCase().includes(query) || n.slug.toLowerCase().includes(query));
    }
    return [200, allNodes.slice(0, 5)];
  });

  mock.onGet('/api/users/me/dashboard').reply(200, {
    dependentActivity: mockData.MOCK_DEPENDENT_ACTIVITY,
    urgentAnnouncements: mockData.MOCK_URGENT_ANNOUNCEMENTS,
    upcomingEvents: mockData.MOCK_UPCOMING_EVENTS
  });

  // Public articles feed (returns published admin articles + seed mock articles)
  mock.onGet('/api/public/articles').reply(() => {
    const adminPublished = adminArticles.filter((a) => a.published !== false);
    return [200, [...adminPublished, ...mockData.MOCK_ARTICLES]];
  });
  mock.onGet(/\/api\/public\/articles\/[^/]+$/).reply((config) => {
    const slug = config.url?.split('/').pop();
    const all = [...adminArticles.filter((a) => a.published !== false), ...mockData.MOCK_ARTICLES];
    const article = all.find((a: any) => a.slug === slug);
    return article ? [200, article] : [404, { message: 'Article not found' }];
  });

  // Admin articles CRUD
  mock.onGet('/api/admin/articles').reply(() => {
    const seedAsPublished = mockData.MOCK_ARTICLES.map((a: any) => ({ ...a, published: true }));
    return [200, [...adminArticles, ...seedAsPublished]];
  });
  mock.onPost('/api/admin/articles').reply((config) => {
    const data = JSON.parse(config.data);
    const newArticle = {
      ...data,
      id: `article-${Date.now()}`,
      date: data.date || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    };
    adminArticles.unshift(newArticle);
    return [201, newArticle];
  });
  mock.onPut(/\/api\/admin\/articles\/[^/]+$/).reply((config) => {
    const id = config.url?.split('/').pop();
    const data = JSON.parse(config.data);
    const idx = adminArticles.findIndex((a) => a.id === id);
    if (idx !== -1) {
      adminArticles[idx] = { ...adminArticles[idx], ...data };
      return [200, adminArticles[idx]];
    }
    // Fall back to seed articles — promote to adminArticles so it's mutable
    const seedIdx = mockData.MOCK_ARTICLES.findIndex((a: any) => a.id === id);
    if (seedIdx !== -1) {
      const updated = { ...mockData.MOCK_ARTICLES[seedIdx], ...data, published: true };
      adminArticles.unshift(updated);
      mockData.MOCK_ARTICLES.splice(seedIdx, 1);
      return [200, updated];
    }
    return [404, { message: 'Article not found' }];
  });
  mock.onDelete(/\/api\/admin\/articles\/[^/]+$/).reply((config) => {
    const id = config.url?.split('/').pop();
    const before = adminArticles.length;
    const keep = adminArticles.filter((a) => a.id !== id);
    if (keep.length < before) {
      adminArticles.splice(0, adminArticles.length, ...keep);
      return [204];
    }
    const seedIdx = mockData.MOCK_ARTICLES.findIndex((a: any) => a.id === id);
    if (seedIdx !== -1) {
      mockData.MOCK_ARTICLES.splice(seedIdx, 1);
      return [204];
    }
    return [404];
  });
  
  // Database index synchronization
  mock.onPost(/\/api\/admin\/database\/indexes\/sync(?:\?.*)?$/i).reply(200, {
    collectionsProcessed: 28,
    totalIndexesCreated: 42,
    details: {
      transactions: ['nodeId_1', 'createdBy_1', 'timestamp_-1'],
      pulse_alerts: ['nodeId_1', 'createdAt_-1'],
      events: ['nodeId_1', 'ownerNodeId_1', 'startTime_1'],
      users: ['clerkId_1', 'email_1'],
      nodes: ['slug_1', 'domain_1'],
      members: ['nodeId_1', 'userId_1']
    },
    completedAt: new Date().toISOString()
  });

  const mockContactInquiries = [
    {
      id: 'inq-1',
      firstName: 'Sarah',
      lastName: 'Connor',
      email: 'sarah.connor@example.com',
      type: 'Sports League',
      message: 'Looking to set up OrgSets for our regional youth swim league across 14 teams.',
      ipAddress: '192.168.1.42',
      createdAt: new Date(Date.now() - 3600000 * 4).toISOString()
    },
    {
      id: 'inq-2',
      firstName: 'Marcus',
      lastName: 'Vance',
      email: 'm.vance@austinhoa.org',
      type: 'Hobbyist Group',
      message: 'Need help evaluating the ledger module for our community dues.',
      ipAddress: '10.0.0.15',
      createdAt: new Date(Date.now() - 3600000 * 28).toISOString()
    }
  ];

  mock.onGet(/\/api\/admin\/contact-inquiries(?:\?.*)?$/i).reply(() => {
    return [200, {
      content: mockContactInquiries,
      totalElements: mockContactInquiries.length,
      totalPages: 1,
      size: 20,
      number: 0
    }];
  });

  mock.onPost('/api/public/contact').reply((config) => {
    try {
      const data = JSON.parse(config.data);
      mockContactInquiries.unshift({
        id: 'inq-' + Date.now(),
        firstName: data.firstName || 'Anonymous',
        lastName: data.lastName || '',
        email: data.email || '',
        type: data.type || 'General',
        message: data.message || '',
        ipAddress: '127.0.0.1',
        createdAt: new Date().toISOString()
      });
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [200, { success: true, message: 'Your inquiry has been submitted.' }];
  });

  const mockBlacklistedIps = ['192.168.1.55', '10.0.0.12'];

  mock.onGet('/api/admin/audit/blacklist/ip').reply(() => [200, [...mockBlacklistedIps]]);

  mock.onPost('/api/admin/audit/blacklist/ip').reply((config) => {
    try {
      const data = JSON.parse(config.data);
      const ip = data.ipAddress || data.ip;
      if (ip && !mockBlacklistedIps.includes(ip)) {
        mockBlacklistedIps.push(ip);
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [200, { success: true }];
  });

  mock.onDelete(/\/api\/admin\/audit\/blacklist\/ip\/(.+)$/).reply((config) => {
    const ip = config.url?.split('/').pop();
    if (ip) {
      const decoded = decodeURIComponent(ip);
      const idx = mockBlacklistedIps.indexOf(decoded);
      if (idx !== -1) mockBlacklistedIps.splice(idx, 1);
    }
    return [204];
  });

  const mockAdminAuditLogs = [
    {
      id: 'log-1',
      userId: 'user_admin_01',
      actorName: 'Marcus Vance',
      actorEmail: 'm.vance@orgsets.com',
      nodeId: 'platform-root',
      nodeName: 'Platform Root',
      nodeSlug: 'global',
      action: 'SYSTEM_SETTINGS_UPDATE',
      targetId: 'platform_config',
      targetName: 'Platform Configuration',
      timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
      metadata: { field: 'maintenance_mode', changedTo: 'false' },
      status: 'SUCCESS'
    },
    {
      id: 'log-2',
      userId: 'user_support_02',
      actorName: 'Elena Rostova',
      actorEmail: 'e.rostova@orgsets.com',
      nodeId: 'node-1',
      nodeName: 'PNW Running Network',
      nodeSlug: 'pnw-running',
      action: 'MEMBER_ROLE_CHANGE',
      targetId: 'user_member_44',
      targetName: 'Sarah Jenkins (Member #44)',
      timestamp: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
      metadata: { role: 'PRESIDENT' },
      status: 'SUCCESS'
    },
    {
      id: 'log-3',
      userId: 'user_super_admin',
      actorName: 'Devon Hayes (Super Admin)',
      actorEmail: 'd.hayes@orgsets.com',
      nodeId: 'platform-root',
      nodeName: 'Platform Root',
      nodeSlug: 'global',
      action: 'IMPERSONATE_USER',
      targetId: 'user_member_12',
      targetName: 'Michael Chang (m.chang@example.com)',
      timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
      metadata: { reason: 'Investigating billing dispute' },
      status: 'SUCCESS'
    },
    {
      id: 'log-4',
      userId: 'user_admin_01',
      actorName: 'Marcus Vance',
      actorEmail: 'm.vance@orgsets.com',
      nodeId: 'node-2',
      nodeName: 'Downtown HOA Alliance',
      nodeSlug: 'downtown-hoa-alliance',
      action: 'NODE_CREATED',
      targetId: 'node-2',
      targetName: 'Downtown HOA Alliance',
      timestamp: new Date(Date.now() - 1000 * 3600 * 12).toISOString(),
      metadata: { slug: 'downtown-hoa-alliance', tier: 'FOUNDATION' },
      status: 'SUCCESS'
    },
    {
      id: 'log-5',
      userId: 'user_member_88',
      actorName: 'Avery Martinez',
      actorEmail: 'avery.m@example.com',
      nodeId: 'node-2',
      nodeName: 'Downtown HOA Alliance',
      nodeSlug: 'downtown-hoa-alliance',
      action: 'MEMBER_JOINED',
      targetId: 'node-2',
      targetName: 'Downtown HOA Alliance',
      timestamp: new Date(Date.now() - 1000 * 3600 * 20).toISOString(),
      metadata: { inviteCode: 'INV-8821' },
      status: 'SUCCESS'
    },
    {
      id: 'log-6',
      userId: 'user_super_admin',
      actorName: 'Devon Hayes (Super Admin)',
      actorEmail: 'd.hayes@orgsets.com',
      nodeId: 'node-1',
      nodeName: 'PNW Running Network',
      nodeSlug: 'pnw-running',
      action: 'MODULES_UPDATED',
      targetId: 'node-1',
      targetName: 'PNW Running Network',
      timestamp: new Date(Date.now() - 1000 * 3600 * 24).toISOString(),
      metadata: { enabled: ['PULSE', 'DISCUSSIONS', 'CALENDAR', 'SUPPORT', 'DIRECTORY'], disabled: ['LEDGER'] },
      status: 'SUCCESS'
    },
    {
      id: 'log-7',
      userId: '198.51.100.4',
      actorName: 'Suspicious IP Actor',
      actorEmail: '198.51.100.4',
      nodeId: 'platform-root',
      nodeName: 'Platform Root',
      nodeSlug: 'global',
      action: 'SECURITY_BLOCK_IP',
      targetId: '198.51.100.4',
      targetName: 'IP: 198.51.100.4',
      timestamp: new Date(Date.now() - 1000 * 3600 * 36).toISOString(),
      metadata: { reason: 'Rate limit violation on /api/auth' },
      status: 'BLOCKED'
    },
    {
      id: 'log-8',
      userId: 'system_daemon',
      actorName: 'System Automation Daemon',
      actorEmail: 'system@orgsets.internal',
      nodeId: 'platform-root',
      nodeName: 'Platform Root',
      nodeSlug: 'global',
      action: 'DATABASE_INDEX_SYNC',
      targetId: 'mongodb_indexes',
      targetName: 'MongoDB System Indexes',
      timestamp: new Date(Date.now() - 1000 * 3600 * 48).toISOString(),
      metadata: { indexesCreated: 4 },
      status: 'SUCCESS'
    }
  ];

  mock.onGet(/\/api\/admin\/audit\/logs(?:\?.*)?$/i).reply(200, {
    content: mockAdminAuditLogs,
    totalElements: mockAdminAuditLogs.length,
    totalPages: 1,
    size: 20,
    number: 0
  });

  const mockAdminTickets = [
    {
      id: 'tck-001',
      nodeId: 'node-root',
      requesterId: 'user_101',
      authorName: 'Alex Mercer',
      title: 'Stripe webhook failure on recurring membership dues',
      description: 'Webhook delivery returned 500 error during monthly billing settlement across sub-nodes.',
      category: 'BILLING',
      severity: 'CRITICAL',
      status: 'OPEN',
      createdAt: new Date(Date.now() - 1000 * 3600 * 2).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 3600 * 1).toISOString(),
      escalatedToSystem: true,
      originNodeId: 'node-1',
      originNodeName: 'Metro Athletic Club',
      addressedToName: 'Platform Support'
    },
    {
      id: 'tck-002',
      nodeId: 'node-root',
      requesterId: 'user_102',
      authorName: 'Sarah Jenkins',
      title: 'Admin privileges not applying after role promotion',
      description: 'User was assigned Admin in local chapter but permissions cache seems stale.',
      category: 'TECHNICAL',
      severity: 'HIGH',
      status: 'INVESTIGATING',
      createdAt: new Date(Date.now() - 1000 * 3600 * 14).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 3600 * 4).toISOString(),
      escalatedToSystem: true,
      originNodeId: 'node-2',
      originNodeName: 'Downtown HOA Alliance',
      addressedToName: 'Platform Support'
    },
    {
      id: 'tck-003',
      nodeId: 'node-1',
      requesterId: 'user_103',
      authorName: 'David Vance',
      title: 'Facility booking overlap dispute in joint calendar',
      description: 'Court 2 was reserved simultaneously by two sub-org teams due to race condition.',
      category: 'CONDUCT',
      severity: 'MEDIUM',
      status: 'ESCALATED',
      createdAt: new Date(Date.now() - 1000 * 3600 * 26).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 3600 * 8).toISOString(),
      escalatedToSystem: false,
      originNodeId: 'node-1-sub',
      originNodeName: 'Junior Tennis League',
      addressedToName: 'Metro Athletic Club'
    },
    {
      id: 'tck-004',
      nodeId: 'node-root',
      requesterId: 'user_104',
      authorName: 'Elena Rostova',
      title: 'Database orphaned node cleanup request',
      description: 'Unused test org from migration needs permanent removal.',
      category: 'TECHNICAL',
      severity: 'LOW',
      status: 'RESOLVED',
      createdAt: new Date(Date.now() - 1000 * 3600 * 48).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 3600 * 12).toISOString(),
      resolutionNotes: 'Orphaned node purged successfully by maintenance worker.',
      escalatedToSystem: true,
      originNodeId: 'node-root',
      originNodeName: 'OrgSets Global',
      addressedToName: 'Platform Support'
    }
  ];

  mock.onGet(/\/api\/admin\/tickets(?:\?.*)?$/i).reply(200, {
    content: mockAdminTickets,
    totalElements: mockAdminTickets.length,
    totalPages: 1,
    size: 20,
    number: 0
  });

  mock.onPatch(/\/api\/admin\/tickets\/([^/]+)\/status(?:\?.*)?$/i).reply((config: any) => {
    const url = config.url || '';
    const match = url.match(/\/api\/admin\/tickets\/([^/]+)\/status/i);
    const id = match ? match[1] : null;
    const body = config.data ? JSON.parse(config.data) : {};
    const t = mockAdminTickets.find(item => item.id === id);
    if (t) {
      if (body.status) t.status = body.status;
      if (body.resolutionNotes) t.resolutionNotes = body.resolutionNotes;
      t.updatedAt = new Date().toISOString();
      return [200, t];
    }
    return [200, { success: true }];
  });

  mock.onPost(/\/api\/admin\/nodes\/modules\/backfill-defaults(?:\?.*)?$/i).reply(200, {
    matchedCount: 1,
    modifiedCount: 1,
    message: 'Default modules successfully synchronized for existing organizations'
  });


  mock.onPost(/\/api\/nodes$/).reply(201, (config: any) => {
    const data = JSON.parse(config.data);
    const newNode = {
      ...data,
      id: '6' + Date.now().toString(16) + '00000000000',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      modules: Array.isArray(data.modules) ? data.modules : mockData.MOCK_NODE.modules,
      config: {
        ...mockData.MOCK_NODE.config,
        modules: Array.isArray(data.modules) ? data.modules : mockData.MOCK_NODE.config.modules
      }
    };
    saveCreatedNode(newNode);
    return [201, newNode];
  });

  // Pulse & Newsletter
  mock.onGet(/\/api\/nodes\/[^/]+\/pulse\/active(?:\?.*)?$/).reply(200, mockData.MOCK_ANNOUNCEMENTS);
  mock.onGet(/\/api\/users\/me\/announcements(?:\?.*)?$/).reply(200, mockData.MOCK_ANNOUNCEMENTS);
  mock.onGet(/\/api\/communications\/announcements(?:\?.*)?$/).reply(200, mockData.MOCK_ANNOUNCEMENTS);
  mock.onGet(/\/api\/pulse\/[^/]+(?:\?.*)?$/).reply(200, mockData.MOCK_ANNOUNCEMENTS);
  mock.onPost(/\/api\/pulse\/[^/]+(?:\?.*)?$/).reply(201, (config: any) => [201, JSON.parse(config.data)]);
  mock.onPost(/\/api\/pulse\/[^/]+\/acknowledge(?:\?.*)?$/).reply(200);
  mock.onPost(/\/api\/pulse\/[^/]+\/resolve(?:\?.*)?$/).reply(200);
  mock.onGet(/\/api\/announcements\/[^/]+\/reads(?:\?.*)?$/).reply(200, []);
  mock.onPost(/\/api\/pulse\/[^/]+\/newsletters\/subscribe(?:\?.*)?$/).reply(201);

  // Redundant discussion mocks removed for consolidation above
  mock.onPost(/\/api\/posts\/[^/]+\/comments(?:\?.*)?$/).reply(201, (config: any) => {
    const data = JSON.parse(config.data);
    return [201, { ...data, id: `cmt-${Date.now()}`, createdAt: new Date().toISOString() }];
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/posts(?:\?.*)?$/).reply(201, (config: any) => {
    const data = JSON.parse(config.data);
    return [201, { ...data, id: `post-${Date.now()}` }];
  });

  // Chat & Channels
  mock.onGet(/\/api\/chat\/[^/]+\/channels(?:\?.*)?$/).reply(200, mockData.MOCK_CHANNELS);
  mock.onGet(/\/api\/chat\/channels\/[^/]+\/messages(?:\?.*)?$/).reply(200, mockData.MOCK_MESSAGES);
  mock.onGet(/\/api\/chat\/[^/]+\/messages(?:\?.*)?$/).reply(200, mockData.MOCK_MESSAGES);
  mock.onPost(/\/api\/chat\/channels\/[^/]+\/messages(?:\?.*)?$/).reply(201, (config: any) => {
    const data = JSON.parse(config.data);
    return [201, { ...data, id: `msg-${Date.now()}`, createdAt: new Date().toISOString() }];
  });
  mock.onPatch(/\/api\/messages\/[^/]+(?:\?.*)?$/).reply((config) => {
    const data = JSON.parse(config.data);
    const id = config.url?.split('/').pop();
    return [200, { ...data, id, isEdited: true, updatedAt: new Date().toISOString() }];
  });
  mock.onDelete(/\/api\/messages\/[^/]+(?:\?.*)?$/).reply(200);
  mock.onPost(/\/api\/chat\/[^/]+\/messages(?:\?.*)?$/).reply(201);
  mock.onPost(/\/api\/nodes\/[^/]+\/channels(?:\?.*)?$/).reply(201);

  // Governance
  mock.onGet(/\/api\/elections(?:\?.*)?$/).reply(200, mockData.MOCK_ELECTIONS);
  
  mock.onGet(/\/api\/elections\/[^/]+\/candidates(?:\?.*)?$/).reply((config) => {
    const electionId = config.url?.split('/').slice(-2, -1)[0];
    const candidates = mockData.MOCK_CANDIDATES.filter(c => c.electionId === electionId);
    return [200, candidates];
  });

  mock.onPost(/\/api\/elections(?:\?.*)?$/).reply((config: any) => {
    const data = JSON.parse(config.data || '{}');
    const newElection = {
      ...data,
      id: `election-${Date.now()}`,
      status: data.status || 'ACTIVE',
      totalVotes: 0,
      startDate: data.startDate || new Date().toISOString(),
      endDate: data.endDate || new Date(Date.now() + 86400000 * 7).toISOString()
    };
    mockData.MOCK_ELECTIONS.unshift(newElection);
    if (Array.isArray(data.options)) {
      data.options.forEach((opt: string, idx: number) => {
        mockData.MOCK_CANDIDATES.push({
          id: `cand-${Date.now()}-${idx}`,
          electionId: newElection.id,
          userId: `opt-${idx}`,
          name: opt,
          role: 'Option',
          statement: opt,
          votesReceived: 0
        });
      });
    }
    return [201, newElection];
  });

  mock.onPost(/\/api\/elections\/[^/]+\/candidates(?:\?.*)?$/).reply((config) => {
    const electionId = config.url?.split('/').slice(-2, -1)[0] || '';
    const data = JSON.parse(config.data || '{}');
    const newCandidate = {
      id: `cand-${Date.now()}`,
      electionId,
      userId: data.candidateId || `user-${Date.now()}`,
      name: data.name || data.candidateId || 'Candidate',
      role: data.role || 'Option',
      statement: data.statement || '',
      platform: data.platform || '',
      votesReceived: 0
    };
    mockData.MOCK_CANDIDATES.push(newCandidate);
    return [201, newCandidate];
  });

  mock.onPost(/\/api\/elections\/[^/]+\/nominate(?:\?.*)?$/).reply((config) => {
    const electionId = config.url?.split('/').slice(-2, -1)[0] || '';
    const data = JSON.parse(config.data);
    const newCandidate = {
      ...data,
      id: `cand-${Date.now()}`,
      electionId,
      userId: 'me',
      name: 'Current User',
      votesReceived: 0
    };
    mockData.MOCK_CANDIDATES.push(newCandidate);
    return [201, newCandidate];
  });

  mock.onPost(/\/api\/elections\/[^/]+\/vote(?:\?.*)?$/).reply((config: any) => {
    const electionId = config.url?.split('?')[0].split('/').filter(Boolean).slice(-2, -1)[0];
    const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
    let candidateId = urlParams.get('candidateId') || config.params?.candidateId;
    if (!candidateId && config.data) {
      try {
        const body = JSON.parse(config.data);
        candidateId = body.candidateId;
      } catch {
        // Unparseable body: keep the existing candidateId.
      }
    }
    
    // Check if already voted
    if (mockData.MOCK_MEMBER_VOTES['me'].includes(electionId)) {
      return [400, { message: 'You have already voted in this election.' }];
    }

    const candidate = mockData.MOCK_CANDIDATES.find(c => c.id === candidateId);
    if (candidate) {
      candidate.votesReceived = (candidate.votesReceived || 0) + 1;
      mockData.MOCK_MEMBER_VOTES['me'].push(electionId);
      return [200, { success: true }];
    }
    return [404, { message: 'Candidate not found' }];
  });

  // Ledger & Payments
  mock.onGet(/\/api\/ledger\/[^/]+\/stats(?:\?.*)?$/).reply((config) => {
    const isMe = config.url?.includes('/stats/me');
    const dataToUse = isMe ? mockData.MOCK_TRANSACTIONS.filter((t: any) => t.userId === getMockUserId()) : mockData.MOCK_TRANSACTIONS;
    const total = dataToUse.reduce((acc: number, t: any) => acc + (t.status === 'PAID' ? t.amount : 0), 0);
    const pending = dataToUse.reduce((acc: number, t: any) => acc + (t.status === 'PENDING' ? t.amount : 0), 0);
    return [200, { total, pending, count: dataToUse.length }];
  });

  mock.onGet(/\/api\/ledger\/[^/]+\/transactions(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const recursive = config.params?.includeSubnodes === 'true';
    let transactions = mockData.MOCK_TRANSACTIONS;
    
    if (nodeId && nodeId !== 'all') {
      if (recursive) {
        transactions = mockData.MOCK_TRANSACTIONS;
      } else {
        transactions = mockData.MOCK_TRANSACTIONS.filter(t => t.nodeId === nodeId);
      }
    }

    return [200, paginate(transactions)];
  });

  mock.onPost(/\/api\/ledger\/[^/]+\/charge(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0] || '60c72b2f9b1d8b2a4c8e4d01';
    const { amount, description, isAnonymous, onBehalfOfNotes } = JSON.parse(config.data);
    const userId = getMockUserId();
    const member = mockData.MOCK_DIRECTORY.find(m => m.userId === userId);
    const userName = member ? member.name : 'James Avery';

    const txId = `tx-${Date.now()}`;
    const newTx: any = {
      id: txId,
      nodeId,
      nodeName: mockData.NODES.find(n => n.id === nodeId)?.name || 'Demo Organization',
      userId,
      userName,
      amount,
      description,
      status: 'PAID',
      createdAt: new Date().toISOString(),
      stripePaymentIntentId: `pi_${Date.now()}_secret_${Math.random().toString(36).substring(7)}`,
      isAnonymous,
      onBehalfOfNotes
    };

    mockData.MOCK_TRANSACTIONS.unshift(newTx);

    return [200, {
      clientSecret: newTx.stripePaymentIntentId,
      amount,
      description,
      isAnonymous,
      onBehalfOfNotes,
      status: 'requires_payment_method'
    }];
  });

  mock.onPost(/\/api\/ledger\/[^/]+\/split(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const { amount } = JSON.parse(config.data);
    
    const node = mockData.NODES.find(n => n.id === nodeId) || mockData.MOCK_NODE;
    const tier = node.tier;
    
    let platformFeeCents = 50; // LOCAL
    if (tier === 'FOUNDATION') platformFeeCents = 40;
    if (tier === 'MOMENTUM') platformFeeCents = 30;
    if (tier === 'INSTITUTIONAL') platformFeeCents = 20;
    
    const platformFee = platformFeeCents / 100;
    const remainingAfterPlatform = amount - platformFee;
    
    const mockSplits = [
      { nodeId: nodeId || 'local', nodeName: node.name, amount: remainingAfterPlatform * 0.8, nodeColor: '#1A56A0', percentage: 80 },
      { nodeId: 'parent', nodeName: 'Grand Council', amount: remainingAfterPlatform * 0.2, nodeColor: '#0F1F3D', percentage: 20 },
      { nodeId: 'platform', nodeName: 'OrgSets Platform (Flat Fee)', amount: platformFee, nodeColor: '#4285F4', percentage: (platformFee / amount) * 100 },
    ];
    return [200, mockSplits];
  });

  // --- Discovery API Mocks ---
  mock.onGet(/\/api\/discovery\/suggest(?:\?.*)?$/).reply((config) => {
    const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
    const query = (urlParams.get('query') || config.params?.query || '').toLowerCase();
    
    let allNodes = [...getCreatedNodes(), ...mockData.NODES];
    if (query) {
      allNodes = allNodes.filter(n => n.name.toLowerCase().includes(query) || n.slug.toLowerCase().includes(query));
    }
    
    // Suggest usually returns a flat array of nodes, not paginated
    return [200, allNodes.slice(0, 5)];
  });

  mock.onGet(/\/api\/discovery\/search(?:\?.*)?$/).reply((config) => {
    const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
    const query = (urlParams.get('query') || config.params?.query || '').toLowerCase();
    
    let allNodes = [...getCreatedNodes(), ...mockData.NODES];
    if (query) {
      allNodes = allNodes.filter(n => n.name.toLowerCase().includes(query) || n.slug.toLowerCase().includes(query));
    }
    
    // Search is paginated
    return [200, paginate(allNodes)];
  });
  // ---------------------------

  mock.onGet(/\/api\/ledger\/[^/]+\/export(?:\?.*)?$/).reply((config) => {
    const headers = ['Date', 'Member', 'Amount', 'Description', 'Status'];
    const rows = mockData.MOCK_TRANSACTIONS.map(t => [
      t.createdAt,
      t.isAnonymous ? 'Anonymous Benefactor' : t.userName,
      t.amount.toString(),
      t.description,
      t.status
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    return [200, csv];
  });

  mock.onPost(/\/api\/ledger\/[^/]+\/sponsor(?:\?.*)?$/).reply(200);
  mock.onGet(/\/api\/ledger\/me\/balances(?:\?.*)?$/).reply(200, []);
  mock.onGet(/\/api\/ledger\/me\/sponsorships(?:\?.*)?$/).reply(200, paginate([]));
  mock.onPost(/\/api\/ledger\/[^/]+\/contribution(?:\?.*)?$/).reply(200);
  mock.onPost(/\/api\/ledger\/[^/]+\/payments(?:\?.*)?$/).reply(200, { success: true });

  mock.onGet(/\/api\/nodes\/[^/]+\/contributions(?:\?.*)?$/).reply((config) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const contributions = mockData.MOCK_CONTRIBUTIONS.filter(c => c.nodeId === nodeId);
    return [200, contributions];
  });

  // Stripe Connect
  mock.onGet(/\/api\/nodes\/[^/]+\/stripe\/connect-link(?:\?.*)?$/).reply(200, {
    url: 'https://connect.stripe.com/oauth/authorize?response_type=code&client_id=ca_mock123&scope=read_write&state=mock-state'
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/stripe\/verify-oauth(?:\?.*)?$/).reply(200, {
    success: true,
    message: "Stripe account connected successfully",
    stripeAccountId: "acct_123abc"
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/stripe\/dashboard-link(?:\?.*)?$/).reply(200, {
    url: '/settings/stripe/mock-dashboard'
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/stripe\/subscribe(?:\?.*)?$/).reply((config) => {
    const data = JSON.parse(config.data);
    const urlParts = config.url?.split('/') || [];
    const nodeId = urlParts[urlParts.length - 3];
    const created = getCreatedNodes();
    const node = created.find(n => n.id === nodeId);
    if (node) {
      node.tier = data.tier;
      node.stripeSubscriptionId = 'sub_mock_' + Date.now();
      node.stripeCustomerId = 'cus_mock_' + Date.now();
      node.subscriptionStatus = 'active';
      node.currentPeriodEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      saveCreatedNode(node);
    }
    return [200, {
      url: `/stripe/callback?scope=read_write&code=mock_sub_code&state=${nodeId}`
    }];
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/stripe\/portal(?:\?.*)?$/).reply((config) => {
    const data = JSON.parse(config.data);
    return [200, {
      url: data.returnUrl || '/'
    }];
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/stripe\/cancel(?:\?.*)?$/).reply((config) => {
    const urlParts = config.url?.split('/') || [];
    const nodeId = urlParts[urlParts.length - 3];
    const created = getCreatedNodes();
    const node = created.find(n => n.id === nodeId);
    if (node) {
      node.subscriptionStatus = 'canceling';
      saveCreatedNode(node);
    }
    return [200, {
      success: true,
      message: "Subscription scheduled to cancel at the end of the billing period. Access will be maintained until the period ends."
    }];
  });

  // Requirements
  mock.onGet(/\/api\/nodes\/[^/]+\/requirements$/).reply(200, mockData.MOCK_REQUIREMENT_GROUPS);
  mock.onGet(/\/api\/nodes\/[^/]+\/requirement-groups(?:\?.*)?$/i).reply(200, mockData.MOCK_REQUIREMENT_GROUPS);
  mock.onGet(/\/api\/nodes\/[^/]+\/requirements\/me$/).reply(200, mockData.MOCK_REQUIREMENT_GROUPS);
  mock.onGet(/\/api\/nodes\/[^/]+\/requirements\/pending$/).reply(200, mockData.MOCK_SUBMISSIONS);
  mock.onGet(/\/api\/nodes\/[^/]+\/evidence\/pending(?:\?.*)?$/).reply(200, mockData.MOCK_SUBMISSIONS);
  mock.onGet(/\/api\/requirements\/[^/]+\/submissions$/).reply(200, mockData.MOCK_SUBMISSIONS);
  mock.onPost(/\/api\/requirements\/[^/]+\/submit$/).reply((config: any) => {
    const requirementId = config.url?.split('/').slice(-2, -1)[0];
    // In a real app, we'd handle the file upload and check storage tier limits here
    return [201, { id: `sub-${Date.now()}`, requirementId, status: 'PENDING' }];
  });
  mock.onPost(/\/api\/submissions\/[^/]+\/verify$/).reply((config: any) => {
    const submissionId = config.url?.split('/').slice(-2, -1)[0];
    const { status, feedback } = JSON.parse(config.data);
    return [200, { id: submissionId, status, feedback, verifiedAt: new Date().toISOString() }];
  });

  // Goals
  mock.onGet(/\/api\/nodes\/[^/]+\/goals$/).reply(200, mockData.MOCK_GOALS);
  mock.onPost(/\/api\/nodes\/[^/]+\/goals$/).reply((config) => {
    const data = JSON.parse(config.data);
    return [201, { ...data, id: `goal-${Date.now()}` }];
  });
  mock.onPatch(/\/api\/goals\/[^/]+$/).reply((config) => {
    const goalId = config.url?.split('/').pop();
    const data = JSON.parse(config.data);
    return [200, { ...data, id: goalId }];
  });

  // Vault
  mock.onPost(/\/api\/vault\/[^/]+\/upload(?:\?.*)?$/).reply((config) => {
    return [201, { content: [{ id: `doc-${Date.now()}`, title: config.params?.title || 'Unknown', description: config.params?.description, visibility: config.params?.visibility || 'PRIVATE', fileSize: 1024 * 1024, fileType: 'application/pdf', uploadedByName: 'Test User', createdAt: new Date().toISOString() }] }];
  });
  mock.onGet(/\/api\/vault\/[^/]+\/documents(?:\?.*)?$/).reply(200, { content: mockData.MOCK_VAULT_ITEMS, totalElements: mockData.MOCK_VAULT_ITEMS.length, totalPages: 1, size: 20 });
  mock.onDelete(/\/api\/vault\/items\/[^/]+(?:\?.*)?$/).reply(204);
  mock.onPatch(/\/api\/vault\/documents\/[^/]+\/verify(?:\?.*)?$/).reply(200);

  // System Admin & Actuator Health
  mock.onGet(/\/api\/health$/).reply(200, { status: 'UP', services: { db: 'UP', auth: 'UP' } });

  mock.onGet(/\/api\/admin\/users(?:\?.*)?$/).reply((config) => {
    return [200, paginate(
      mockData.MOCK_USERS.map(u => ({
        id: u.id,
        email: u.email,
        fullName: u.name,
        isSuperAdmin: u.id === 'user-system',
        active: true,
        role: u.id === 'user-system' ? 'SUPER_ADMIN' : 'USER'
      }))
    )];
  });

  mock.onGet(/\/api\/admin\/nodes(?:\?.*)?$/).reply(200, paginate(mockData.NODES));

  // Platform roles & impersonation
  mock.onPatch(/\/api\/admin\/users\/[^/]+\/platform-role$/).reply(200);
  mock.onPost(/\/api\/admin\/users\/[^/]+\/impersonate$/).reply(200, { success: true });

  // MFA & Verify Email
  mock.onDelete(/\/api\/admin\/users\/[^/]+\/auth\/mfa$/).reply(204);
  mock.onPost(/\/api\/admin\/users\/[^/]+\/auth\/verify-email$/).reply(200);

  // Status & GDPR Purge & Export
  mock.onPatch(/\/api\/admin\/users\/[^/]+\/status$/).reply(200);
  mock.onPost(/\/api\/admin\/platform\/purge$/).reply(200);
  mock.onPost(/\/api\/admin\/platform\/export$/).reply(200);
  mock.onPost(/\/api\/admin\/platform\/maintenance$/).reply(200);

  // Manual Roster Placement & Dues & Family Linkage
  mock.onPost(/\/api\/admin\/nodes\/([^/]+)\/members\/([^/]+)\/manual-placement$/).reply((config) => {
    const match = config.url?.match(/\/api\/admin\/nodes\/([^/]+)\/members\/([^/]+)\/manual-placement$/);
    const nodeId = match ? match[1] : '';
    const userId = match ? match[2] : '';
    let body: any = {};
    try {
      body = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    } catch {
      // Unparseable body: fall back to {} and default the role below.
    }
    const role = body.role || 'ADMIN';
    const existing = mockData.MOCK_DIRECTORY.find(m => (m.nodeId === nodeId || !m.nodeId) && (m.userId === userId || m.id === userId));
    if (existing) {
      existing.role = role as any;
      existing.nodeId = nodeId;
    } else {
      mockData.MOCK_DIRECTORY.push({
        id: `mem-${Date.now()}`,
        userId: userId,
        name: userId.startsWith('user_') ? `User ${userId.slice(-6)}` : userId,
        email: `${userId.toLowerCase().replace(/[^a-z0-9]/g, '')}@example.com`,
        role: role as any,
        nodeId: nodeId,
        nodeName: 'Organization Node',
        joinedAt: new Date().toISOString(),
        skills: ['Admin'],
        metadata: { status: 'ACTIVE' },
        memberType: 'PRIMARY'
      });
    }
    return [200, { success: true, nodeId, userId, role }];
  });
  mock.onPost(/\/api\/admin\/nodes\/[^/]+\/members\/[^/]+\/waive-dues$/).reply(200);
  mock.onPost(/\/api\/admin\/users\/[^/]+\/dependents\/[^/]+$/).reply(200);
  mock.onDelete(/\/api\/admin\/users\/[^/]+\/dependents\/[^/]+$/).reply(204);

  // Moderation & Quarantine
  mock.onPost(/\/api\/admin\/moderation\/users\/[^/]+\/mute$/).reply(200);
  mock.onPatch(/\/api\/admin\/moderation\/content\/quarantine$/).reply(200);

  // Chronological User Event Timeline
  mock.onGet(/\/api\/admin\/telemetry\/users\/[^/]+\/events(?:\?.*)?$/).reply(200, [
    { timestamp: new Date(Date.now() - 3600000).toISOString(), ipAddress: '192.168.1.55', action: 'Failed login attempt', target: 'MFA Gate' },
    { timestamp: new Date(Date.now() - 7200000).toISOString(), ipAddress: '192.168.1.55', action: 'Payment success webhook', target: '#INV-123' },
    { timestamp: new Date(Date.now() - 10800000).toISOString(), ipAddress: '192.168.1.55', action: 'Clicked pay dues', target: 'Chapter 4' }
  ]);

  // Billing Node status & transaction override & refund
  mock.onPatch(/\/api\/admin\/billing\/nodes\/[^/]+\/payout-status$/).reply(200);
  mock.onPatch(/\/api\/admin\/billing\/transactions\/[^/]+\/manual-status$/).reply((config) => {
    const segments = config.url?.split('/') || [];
    const txId = segments[segments.length - 2];
    const { status } = JSON.parse(config.data);
    const tx = mockData.MOCK_TRANSACTIONS.find(t => t.id === txId);
    if (tx) {
      tx.status = status as any;
      return [200, tx];
    }
    return [404, { message: 'Transaction not found' }];
  });
  mock.onPost(/\/api\/admin\/billing\/refund$/).reply((config) => {
    const { transactionId } = JSON.parse(config.data);
    const tx = mockData.MOCK_TRANSACTIONS.find(t => t.id === transactionId);
    if (tx) {
      tx.status = 'REFUNDED';
      return [200, tx];
    }
    return [404, { message: 'Transaction not found' }];
  });

  // Lineage Split Simulator
  mock.onGet(/\/api\/admin\/lineage\/[^/]+$/).reply(200, {
    nodeId: '60c72b2f9b1d8b2a4c8e4d01',
    lineage: mockData.NODES
  });
  mock.onPost(/\/api\/admin\/lineage\/[^/]+\/simulate-split$/).reply(200, [
    { nodeId: '60c72b2f9b1d8b2a4c8e4d01', nodeName: 'Local Chapter', amount: 40.00, percentage: 80 },
    { nodeId: '60c72b2f9b1d8b2a4c8e4d02', nodeName: 'Grand Council', amount: 10.00, percentage: 20 }
  ]);

  // Service delivery gateway monitoring
  mock.onGet(/\/api\/admin\/monitoring\/delivery$/).reply(200, {
    smsSuccessRate: 99.8,
    emailSuccessRate: 99.5,
    logs: [
      { type: 'SMS', status: 'DELIVERED', messageId: 'msg-1' },
      { type: 'EMAIL', status: 'DELIVERED', messageId: 'email-1' }
    ]
  });

  // Database Orphans Audit & Cleanup
  const mockDbOrphanRecords = {
    members: 2,
    invitations: 0,
    nodeRoles: 1,
    posts: 3,
    pulseAlerts: 0,
    newsletters: 0,
    channels: 1,
    messageBoardTopics: 0,
    vaultAssets: 1,
    orgEvents: 0,
    goals: 0,
    governanceRules: 0,
    elections: 0,
    contributions: 0,
    transactions: 0,
    complianceRequirements: 0,
    complianceEvidence: 0,
    supportTickets: 0,
    memberRelationships: 0,
    requirementGroups: 0,
    auditLogs: 0,
    dataExports: 0,
    totalOrphanedRecords: 7
  };

  mock.onGet(/\/api\/admin\/nodes\/orphans\/records(?:\?.*)?$/).reply(200, mockDbOrphanRecords);

  mock.onGet(/\/api\/admin\/nodes\/orphans(?:\?.*)?$/).reply((config) => {
    if (config.params?.scope === 'records' || config.url?.includes('scope=records')) {
      return [200, mockDbOrphanRecords];
    }
    return [200, [
      {
        id: 'orphan-node-harborcreek',
        slug: 'harborcreek-swim-team',
        name: 'Harborcreek Swim Team',
        createdAt: '2026-09-04T12:00:00Z',
        createdBy: undefined,
        memberCount: 0
      }
    ]];
  });

  mock.onPost(/\/api\/admin\/nodes\/orphans\/cleanup(?:\?.*)?$/).reply(200, {
    ...mockDbOrphanRecords,
    totalPurgedRecords: 7
  });

  mock.onDelete(/\/api\/admin\/nodes\/orphans\/unowned(?:\?.*)?$/).reply(200, {
    purgedCount: 1,
    purgedNodeIds: ['orphan-node-harborcreek']
  });

  mock.onDelete(/\/api\/admin\/nodes\/orphans(?:\?.*)?$/).reply(200, {
    ...mockDbOrphanRecords,
    totalPurgedRecords: 7
  });

  mock.onPost(/\/api\/admin\/nodes\/[^/]+\/reassign-owner(?:\?.*)?$/).reply((config) => {
    let targetUserId = 'user_admin';
    try {
      const parsed = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
      if (parsed?.targetUserId) targetUserId = parsed.targetUserId;
    } catch {
      // Unparseable body: keep the default targetUserId.
    }
    return [200, {
      id: 'orphan-node-harborcreek',
      slug: 'harborcreek-swim-team',
      name: 'Harborcreek Swim Team',
      tier: 'LOCAL',
      visibility: 'PUBLIC',
      createdBy: targetUserId
    }];
  });

  // Admin identity & deep sync mocks
  mock.onPost(/\/api\/admin\/sync(?:\?.*)?$/).reply(200, {
    syncedCount: 1,
    message: 'Deep sync completed'
  });

  mock.onPost(/\/api\/admin\/users\/[^/]+\/sync(?:\?.*)?$/).reply((config) => {
    const match = config.url?.match(/\/api\/admin\/users\/([^/]+)\/sync/);
    const clerkId = match ? match[1] : 'user_synced';
    return [200, {
      id: clerkId,
      clerkId: clerkId,
      email: `${clerkId}@example.com`,
      fullName: `Synced User (${clerkId.slice(-6)})`,
      role: 'USER',
      active: true
    }];
  });

  // Storage audit & Cleanup
  mock.onGet(/\/api\/admin\/storage\/orphans(?:\?.*)?$/).reply(200, {
    orphanedAssetsCount: 1,
    orphanedBytes: 3145728,
    unreferencedS3FilesCount: 0,
    orphanedAssets: [
      {
        id: 'asset-123',
        fileName: 'report.pdf',
        nodeId: 'deleted-node-456',
        size: 3145728,
        url: 'vault/report.pdf'
      }
    ]
  });
  mock.onDelete(/\/api\/admin\/storage\/orphans(?:\?.*)?$/).reply(200, {
    deletedAssetsCount: 1,
    reclaimedBytes: 3145728,
    deletedS3ObjectsCount: 1,
    reconciledNodesCount: 2
  });


  // Audit Logs & IP Blacklist
  mock.onGet(/\/api\/admin\/audit\/logs(?:\?.*)?$/).reply(200, paginate([
    { id: 'a1', timestamp: new Date().toISOString(), actor: 'System Admin', action: 'MEMBER_MUTED', target: 'Sarah Jenkins', status: 'SUCCESS', ipAddress: '192.168.1.1' },
    { id: 'a2', timestamp: new Date().toISOString(), actor: 'John Smith', action: 'MEMBER_REMOVED', target: 'Jane Doe', status: 'SUCCESS', ipAddress: '192.168.1.1' }
  ]));
  mock.onPost(/\/api\/admin\/audit\/blacklist\/ip$/).reply(200);

  // Platform Deep Sync
  mock.onPost(/\/api\/admin\/sync(?:\?.*)?$/).reply(200, {
    status: 'COMPLETED',
    collectionsIndexed: 14,
    indexesEnsured: 32,
    nodesReconciled: 4,
    totalStorageBytes: 52428800,
    durationMs: 345,
    timestamp: new Date().toISOString()
  });

  // Guided Setup Templates
  const adminTemplates = [
    {
      id: 'youth-sports',
      name: 'Youth Sports League',
      status: 'ACTIVE',
      activeInstallations: 42,
      terminology: {
        member: 'Athlete',
        parentNode: 'League',
        childNode: 'Team'
      },
      modules: ['roster', 'dues', 'schedules', 'waivers', 'events']
    },
    {
      id: 'hoa',
      name: 'Homeowners Association (HOA)',
      status: 'ACTIVE',
      activeInstallations: 28,
      terminology: {
        member: 'Resident',
        parentNode: 'Master Association',
        childNode: 'Neighborhood Sub-Division'
      },
      modules: ['roster', 'dues', 'events', 'waivers', 'elections']
    },
    {
      id: 'union',
      name: 'Labor Union & Trade Guild',
      status: 'ACTIVE',
      activeInstallations: 15,
      terminology: {
        member: 'Union Member',
        parentNode: 'National Union',
        childNode: 'Local Chapter'
      },
      modules: ['roster', 'dues', 'elections', 'events']
    },
    {
      id: 'greek-life',
      name: 'Fraternity & Sorority',
      status: 'ACTIVE',
      activeInstallations: 19,
      terminology: {
        member: 'Brother/Sister',
        parentNode: 'National Council',
        childNode: 'Collegiate Chapter'
      },
      modules: ['roster', 'dues', 'events', 'elections']
    },
    {
      id: 'church',
      name: 'Faith & Religious Congregation',
      status: 'ACTIVE',
      activeInstallations: 12,
      terminology: {
        member: 'Congregant',
        parentNode: 'Diocese/Synod',
        childNode: 'Parish/Ministry'
      },
      modules: ['roster', 'dues', 'events', 'waivers']
    }
  ];

  mock.onGet(/\/api\/admin\/templates(?:\?.*)?$/).reply(() => {
    return [200, adminTemplates];
  });
  mock.onPut(/\/api\/admin\/templates\/([^/]+)$/).reply((config) => {
    const urlMatch = config.url?.match(/\/api\/admin\/templates\/([^/?]+)/);
    const templateId = urlMatch ? urlMatch[1] : '';
    let payload: any = {};
    try {
      payload = JSON.parse(config.data || '{}');
    } catch {
      payload = {};
    }
    const idx = adminTemplates.findIndex(t => t.id === templateId);
    if (idx >= 0) {
      adminTemplates[idx] = { ...adminTemplates[idx], ...payload };
      return [200, adminTemplates[idx]];
    } else {
      const newTemplate = {
        id: templateId || `template-${Date.now()}`,
        status: 'ACTIVE',
        activeInstallations: 0,
        ...payload
      };
      adminTemplates.push(newTemplate);
      return [200, newTemplate];
    }
  });

  mock.onPost(/\/api\/errors\/report$/).reply((config) => {
    const { message } = JSON.parse(config.data);
    const ticketId = `tkt-${Date.now()}`;
    const newTicket = {
      id: ticketId,
      organization: 'Error Boundary',
      subject: `Crash: ${message}`,
      status: 'OPEN',
      priority: 'CRITICAL',
      submittedAt: new Date().toISOString()
    };
    adminTickets.unshift(newTicket);
    return [200, { success: true, ticketId }];
  });
  mock.onDelete(/\/api\/admin\/nodes\/[^/]+$/).reply(200);
  mock.onPost(/\/api\/admin\/nodes\/[^/]+\/notifications(?:\?.*)?$/i).reply((config) => {
    const params = config.params || {};
    let title = params.title;
    let message = params.message;
    let link = params.link;
    let type = params.type;
    if (config.url?.includes('?')) {
      const searchParams = new URLSearchParams(config.url.split('?')[1]);
      title = title || searchParams.get('title') || undefined;
      message = message || searchParams.get('message') || undefined;
      link = link || searchParams.get('link') || undefined;
      type = type || searchParams.get('type') || undefined;
    }
    const broadcastType = type || 'ANNOUNCEMENT';
    const nodeId = config.url?.split('/')[4] || 'platform-root';
    const broadcastId = `bcast-${Date.now()}`;
    const newNotif = {
      id: `notif-${Date.now()}`,
      userId: getMockUserId(),
      nodeId,
      type: broadcastType,
      title: title || 'Admin Broadcast',
      message: message || '',
      link: link || undefined,
      createdAt: new Date().toISOString(),
      read: false,
      broadcastId
    };
    mockNotifications.unshift(newNotif);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('mock-notification-broadcast', { detail: newNotif }));
    }

    console.log(`[Mock Admin Broadcast] Dispatched to Node: ${nodeId}`, newNotif);
    return [200, { success: true, notification: newNotif }];
  });

  mock.onGet(/\/api\/admin\/notifications\/(?:system-alerts|broadcasts)(?:\?.*)?$/i).reply((config) => {
    const params = config.params || {};
    const page = parseInt(params.page || '0', 10);
    const size = parseInt(params.size || '10', 10);

    const alertMap = new Map<string, any>();
    mockNotifications
      .filter(n => n.broadcastId || ['ANNOUNCEMENT', 'SYSTEM_ALERT', 'BROADCAST', 'ALERT', 'EMERGENCY'].includes(n.type))
      .forEach(n => {
        const key = n.broadcastId || n.id;
        if (!alertMap.has(key)) {
          alertMap.set(key, {
            id: key,
            nodeId: n.nodeId || 'platform-root',
            type: n.type || 'ANNOUNCEMENT',
            title: n.title,
            message: n.message,
            link: n.link,
            createdAt: n.createdAt,
            recipientCount: 1,
            readCount: n.read ? 1 : 0
          });
        } else {
          const item = alertMap.get(key);
          item.recipientCount += 1;
          if (n.read) item.readCount += 1;
        }
      });

    const alerts = Array.from(alertMap.values()).map(alert => {
      const matching = mockNotifications.filter(n => n.broadcastId === alert.id || n.id === alert.id);
      const computedRead = matching.length > 0
        ? matching.filter(n => n.read).length
        : Math.min(alert.recipientCount, 2);
      return {
        ...alert,
        readCount: alert.readCount !== undefined ? alert.readCount : computedRead
      };
    });
    const paginated = alerts.slice(page * size, (page + 1) * size);
    return [200, paginate(paginated, page, size, alerts.length)];
  });

  mock.onDelete(/\/api\/admin\/notifications\/(?:system-alerts|broadcasts)\/[^/?]+(?:\?.*)?$/i).reply((config) => {
    const match = config.url?.match(/\/api\/admin\/notifications\/(?:system-alerts|broadcasts)\/([^/?]+)/i);
    const alertId = match ? match[1] : null;
    if (alertId) {
      for (let i = mockNotifications.length - 1; i >= 0; i--) {
        const n = mockNotifications[i];
        if (n.broadcastId === alertId || n.id === alertId) {
          mockNotifications.splice(i, 1);
        }
      }
    }
    return [200, { success: true }];
  });

  mock.onGet(/\/api\/admin\/notifications\/(?:system-alerts|broadcasts)\/([^/?]+)\/recipients(?:\?.*)?$/i).reply((config) => {
    const match = config.url?.match(/\/api\/admin\/notifications\/(?:system-alerts|broadcasts)\/([^/?]+)\/recipients/i);
    const alertId = match ? match[1] : '';
    const params = config.params || {};
    const page = parseInt(params.page || '0', 10);
    const size = parseInt(params.size || '10', 10);

    const matching = mockNotifications.filter(n => n.broadcastId === alertId || n.id === alertId);
    let recipients: BroadcastRecipient[] = [];

    if (matching.length > 0) {
      recipients = matching.map((n, idx) => ({
        userId: n.userId || `usr_mock_${idx + 1}`,
        name: idx === 0 ? 'Admin Root' : `Member ${idx}`,
        email: idx === 0 ? 'admin@orgsets.com' : `member${idx}@orgsets.com`,
        read: !!n.read,
        readAt: n.readAt || (n.read ? n.createdAt : undefined),
        sentAt: n.createdAt
      }));
    } else {
      recipients = [
        {
          userId: 'usr_admin_1',
          name: 'Richard T. (System Admin)',
          email: 'admin@orgsets.com',
          read: true,
          readAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
          sentAt: new Date(Date.now() - 1000 * 60 * 60).toISOString()
        },
        {
          userId: 'usr_sarah_c',
          name: 'Sarah Connor',
          email: 's.connor@example.org',
          read: true,
          readAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
          sentAt: new Date(Date.now() - 1000 * 60 * 60).toISOString()
        },
        {
          userId: 'usr_marcus_v',
          name: 'Marcus Vance',
          email: 'marcus@example.org',
          read: false,
          readAt: undefined,
          sentAt: new Date(Date.now() - 1000 * 60 * 60).toISOString()
        },
        {
          userId: 'usr_elena_r',
          name: 'Elena Rostova',
          email: 'elena@example.org',
          read: false,
          readAt: undefined,
          sentAt: new Date(Date.now() - 1000 * 60 * 60).toISOString()
        }
      ];
    }

    const paginated = recipients.slice(page * size, (page + 1) * size);
    return [200, paginate(paginated, page, size, recipients.length)];
  });



  // Users & Passport
  mock.onGet(/\/api\/users\/me(?:\?.*)?$/).reply(200, mockData.MOCK_USER_PROFILE);
  mock.onPatch(/\/api\/users\/me(?:\?.*)?$/).reply((config) => {
    const data = JSON.parse(config.data);
    Object.assign(mockData.MOCK_USER_PROFILE, data);
    return [200, mockData.MOCK_USER_PROFILE];
  });
  mock.onGet(/\/api\/users\/me\/preferences(?:\?.*)?$/).reply(200, mockData.MOCK_USER_PREFERENCES);
  mock.onPatch(/\/api\/users\/me\/preferences(?:\?.*)?$/).reply((config) => {
    const data = JSON.parse(config.data);
    if (data.global) {
      mockData.MOCK_USER_PREFERENCES.global = data.global;
    }
    if (data.nodeId && data.nodePreferences) {
      mockData.MOCK_USER_PREFERENCES.nodeSpecific = mockData.MOCK_USER_PREFERENCES.nodeSpecific || {};
      mockData.MOCK_USER_PREFERENCES.nodeSpecific[data.nodeId] = data.nodePreferences;
    }
    return [200, mockData.MOCK_USER_PREFERENCES];
  });
  mock.onGet(/\/api\/users\/me\/passport(?:\?.*)?$/).reply(200, { status: 'PENDING', vettingLevel: 1, credentials: [] });
  mock.onPost(/\/api\/users\/me\/passport\/verify(?:\?.*)?$/).reply(200, { status: 'PENDING', vettingLevel: 1 });

  // Notifications - Reactive mock store
  mock.onGet(/\/api\/notifications\/unread-count(?:\?.*)?$/).reply(() => {
    const count = mockNotifications.filter(n => !n.read).length;
    return [200, { count }];
  });
  mock.onPut(/\/api\/notifications\/[^/]+\/read(?:\?.*)?$/).reply((config) => {
    const parts = config.url?.split('?')[0].split('/') || [];
    const notifId = parts[parts.indexOf('notifications') + 1];
    const target = mockNotifications.find(n => n.id === notifId);
    if (target) target.read = true;
    return [200, { success: true }];
  });
  mock.onAny(/\/api\/notifications\/read-all(?:\?.*)?$/).reply(() => {
    mockNotifications.forEach(n => { n.read = true; });
    return [200, { success: true }];
  });
  mock.onGet(/\/api\/notifications(?:\?.*)?$/).reply((config) => {
    const params = config.params || {};
    const page = parseInt(params.page || '0', 10);
    const size = parseInt(params.size || '20', 10);
    const paginated = mockNotifications.slice(page * size, (page + 1) * size);
    return [200, paginate(paginated, page, size, mockNotifications.length)];
  });
  mock.onGet(/\/api\/notifications\/stream(?:\?.*)?$/).reply(200);

  // Members
  mock.onGet(/\/api\/nodes\/[^/]+\/members\/directory(?:\?.*)?$/).reply((config) => {
    // Support both config.params and URLSearchParams fallback
    const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
    const nameFilter = (config.params?.name || urlParams.get('name') || '').toLowerCase();
    const skillFilter = (config.params?.skill || urlParams.get('skill') || '').toLowerCase();
    const roleParam = config.params?.role || urlParams.get('role');
    const rolesFilter = roleParam ? roleParam.split(',') : [];

    const urlParts = config.url?.split('?')[0].split('/') || [];
    const nodeId = urlParts[urlParts.indexOf('nodes') + 1];

    const userId = getMockUserId();
    const currentUser = mockData.MOCK_USERS.find(u => u.id === userId);
    const createdNodes = getCreatedNodes();
    const isCreatedNode = createdNodes.some(n => n.id === nodeId);

    let filteredMembers = [];
    if (isCreatedNode) {
      filteredMembers = mockData.MOCK_DIRECTORY.filter(m => m.nodeId === nodeId);
    } else {
      filteredMembers = [...mockData.MOCK_DIRECTORY];
    }

    // Determine memberships of the current user to inject them
    const memberships = [...(mockData.MOCK_MEMBERSHIPS_MAP[userId] || [])];
    createdNodes.forEach(node => {
      if (!memberships.find(m => m.id === node.id)) {
        memberships.push({
          id: node.id,
          name: node.name,
          slug: node.slug,
          role: 'ADMIN',
          unreadCount: 0,
          hierarchyPath: '/' + node.name,
          depth: 0,
          description: node.description || 'Custom organization node'
        });
      }
    });

    const userMembership = memberships.find(m => m.id === nodeId);
    
    // Add current user as member if they are not already in the list
    if (userMembership && !filteredMembers.some(m => m.userId === userId)) {
      filteredMembers.unshift({
        id: `mem-${userId}-${nodeId}`,
        userId: userId,
        name: currentUser ? currentUser.name : 'System Admin',
        avatarUrl: currentUser ? currentUser.avatar : 'https://picsum.photos/seed/sysadmin/100/100',
        email: currentUser ? currentUser.email : 'sysadmin@orgsets.com',
        role: userMembership.role || 'ADMIN',
        nodeId: nodeId,
        nodeName: userMembership.name || 'Custom Organization',
        joinedAt: new Date().toISOString(),
        skills: ['Leadership', 'Administration'],
        metadata: { phone: '555-0199', department: 'Executive', status: 'ACTIVE' },
        memberType: 'PRIMARY'
      });
    }

    if (nameFilter) {
      filteredMembers = filteredMembers.filter(m => 
        m.name.toLowerCase().includes(nameFilter) || 
        m.id.toLowerCase().includes(nameFilter) ||
        m.userId.toLowerCase().includes(nameFilter)
      );
    }

    if (skillFilter) {
      filteredMembers = filteredMembers.filter(m => 
        (m.skills || []).some(s => s.toLowerCase().includes(skillFilter))
      );
    }

    if (rolesFilter.length > 0) {
      filteredMembers = filteredMembers.filter(m => rolesFilter.includes(m.role));
    }

    return [200, paginate(filteredMembers)];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/members(?:\?.*)?$/).reply((config) => {
    // Support both config.params and URLSearchParams fallback
    const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
    const nameFilter = (config.params?.name || urlParams.get('name') || '').toLowerCase();
    const roleFilter = config.params?.role || urlParams.get('role');
    const statusFilter = config.params?.status || urlParams.get('status');

    const urlParts = config.url?.split('?')[0].split('/') || [];
    const nodeId = urlParts[urlParts.indexOf('nodes') + 1];

    const userId = getMockUserId();
    const currentUser = mockData.MOCK_USERS.find(u => u.id === userId);
    const createdNodes = getCreatedNodes();
    const isCreatedNode = createdNodes.some(n => n.id === nodeId);

    let filteredMembers = [];
    if (isCreatedNode) {
      filteredMembers = mockData.MOCK_DIRECTORY.filter(m => m.nodeId === nodeId);
    } else {
      filteredMembers = [...mockData.MOCK_DIRECTORY];
    }

    // Determine memberships of the current user to inject them
    const memberships = [...(mockData.MOCK_MEMBERSHIPS_MAP[userId] || [])];
    createdNodes.forEach(node => {
      if (!memberships.find(m => m.id === node.id)) {
        memberships.push({
          id: node.id,
          name: node.name,
          slug: node.slug,
          role: 'ADMIN',
          unreadCount: 0,
          hierarchyPath: '/' + node.name,
          depth: 0,
          description: node.description || 'Custom organization node'
        });
      }
    });

    const userMembership = memberships.find(m => m.id === nodeId);
    
    // Add current user as member if they are not already in the list
    if (userMembership && !filteredMembers.some(m => m.userId === userId)) {
      filteredMembers.unshift({
        id: `mem-${userId}-${nodeId}`,
        userId: userId,
        name: currentUser ? currentUser.name : 'System Admin',
        avatarUrl: currentUser ? currentUser.avatar : 'https://picsum.photos/seed/sysadmin/100/100',
        email: currentUser ? currentUser.email : 'sysadmin@orgsets.com',
        role: userMembership.role || 'ADMIN',
        nodeId: nodeId,
        nodeName: userMembership.name || 'Custom Organization',
        joinedAt: new Date().toISOString(),
        skills: ['Leadership', 'Administration'],
        metadata: { phone: '555-0199', department: 'Executive', status: 'ACTIVE' },
        memberType: 'PRIMARY'
      });
    }

    if (nameFilter) {
      filteredMembers = filteredMembers.filter(m => 
        m.name.toLowerCase().includes(nameFilter) || 
        m.id.toLowerCase().includes(nameFilter) ||
        m.userId.toLowerCase().includes(nameFilter)
      );
    }

    if (roleFilter && roleFilter !== 'ALL') {
      filteredMembers = filteredMembers.filter(m => m.role === roleFilter);
    }

    if (statusFilter && statusFilter !== 'ALL') {
      filteredMembers = filteredMembers.filter(m => (m.metadata?.status || 'ACTIVE') === statusFilter);
    }

    return [200, paginate(filteredMembers)];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/members\/relationships$/).reply(200, paginate(mockData.MOCK_RELATIONSHIPS));
  mock.onDelete(/\/api\/nodes\/[^/]+\/members\/relationships\/[^/]+$/).reply(200);
  mock.onPatch(/\/api\/nodes\/[^/]+\/members\/[^/]+\/role$/).reply(200);
  mock.onPost(/\/api\/nodes\/[^/]+\/members\/invite$/).reply(201, { inviteLink: 'https://...' });

  // Deleted duplicate legacy mappings at bottom

  mock.onGet(/\/api\/users\/me(?:\?.*)?$/).reply(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('mock_user_identity') : null;
    const user = saved ? JSON.parse(saved) : null;
    return [200, user || mockData.MOCK_USER_PROFILE];
  });

  mock.onPatch(/\/api\/users\/me(?:\?.*)?$/).reply((config: any) => {
    const patchData = config.data ? JSON.parse(config.data) : {};
    Object.assign(mockData.MOCK_USER_PROFILE, patchData);
    return [200, { ...mockData.MOCK_USER_PROFILE, ...patchData }];
  });

  mock.onDelete(/\/api\/users\/me\/profile(?:\?.*)?$/).reply(200);

  mock.onPost('/api/users/me/sync').reply(200, (config: any) => {
    return [200, JSON.parse(config.data)];
  });

  // NewsletterStudio Mock Implementation
  mock.onGet(/\/api\/nodes\/[^/]+\/newsletters(?:\?.*)?$/).reply((config: any) => {
    return [200, paginate(mockData.MOCK_NEWSLETTERS)];
  });

  mock.onGet(/\/api\/communications\/[^/]+\/newsletters(?:\?.*)?$/).reply((config: any) => {
    return [200, paginate(mockData.MOCK_NEWSLETTERS)];
  });

  mock.onPost(/\/api\/nodes\/[^/]+\/newsletters(?:\?.*)?$/).reply((config: any) => {
    const nodeId = config.url?.split('/').slice(-2, -1)[0];
    const newNewsletter: Newsletter = {
      id: `news-${Date.now()}`,
      nodeId: nodeId || '60c72b2f9b1d8b2a4c8e4d01',
      authorUserId: getMockUserId(),
      authorName: 'Mock Author',
      title: 'Untitled Newsletter',
      subjectLine: '',
      previewText: '',
      status: 'DRAFT',
      scheduledFor: null,
      publishedAt: null,
      thumbnailUrl: null,
      cascadesDown: true,
      sections: [
        {
          id: `sec-${Date.now()}`,
          type: 'text',
          orderIndex: 0,
          htmlContent: '<p>Start typing your newsletter here...</p>'
        }
      ],
      metrics: {
        recipientsTotal: 0,
        deliveredEmail: 0,
        deliveredSms: 0,
        deliveredPush: 0,
        uniqueOpens: 0,
        linkClicks: 0,
        bounces: 0
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1
    };
    mockData.MOCK_NEWSLETTERS.push(newNewsletter);
    return [201, newNewsletter];
  });

  mock.onGet(/\/api\/newsletters\/[^/]+(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('?')[0].split('/').pop();
    const newsletter = mockData.MOCK_NEWSLETTERS.find(n => n.id === id);
    return newsletter ? [200, newsletter] : [404];
  });

  mock.onPatch(/\/api\/newsletters\/[^/]+\/sections(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('/').slice(-2, -1)[0];
    const { sections, version } = JSON.parse(config.data);
    const newsletter = mockData.MOCK_NEWSLETTERS.find(n => n.id === id);
    
    if (!newsletter) return [404];
    
    // Concurrency Check
    if (version < newsletter.version) {
      return [409, { message: 'Conflict: Older version sent' }];
    }
    
    newsletter.sections = sections;
    newsletter.version = version + 1;
    newsletter.updatedAt = new Date().toISOString();
    
    return [200, newsletter];
  });

  mock.onPatch(/\/api\/newsletters\/[^/]+\/metadata(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('/').slice(-2, -1)[0];
    const data = JSON.parse(config.data);
    const newsletter = mockData.MOCK_NEWSLETTERS.find(n => n.id === id);
    
    if (!newsletter) return [404];
    
    Object.assign(newsletter, data);
    newsletter.updatedAt = new Date().toISOString();
    
    return [200, newsletter];
  });

  mock.onPost(/\/api\/newsletters\/[^/]+\/publish(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('/').slice(-2, -1)[0];
    const newsletter = mockData.MOCK_NEWSLETTERS.find(n => n.id === id);
    
    if (!newsletter) return [404];
    
    newsletter.status = 'PUBLISHING';
    // Simulate background process finish
    setTimeout(() => {
      newsletter.status = 'PUBLISHED';
      newsletter.publishedAt = new Date().toISOString();
    }, 2000);
    
    return [202, { message: 'Job started' }];
  });

  mock.onGet(/\/api\/newsletters\/[^/]+\/stats(?:\?.*)?$/).reply((config) => {
    const id = config.url?.split('/').slice(-2, -1)[0];
    const newsletter = mockData.MOCK_NEWSLETTERS.find(n => n.id === id);
    return newsletter ? [200, newsletter.metrics] : [404];
  });

  mock.onPost(/\/api\/newsletters\/[^/]+\/images(?:\?.*)?$/).reply((config) => {
    // Simulate image upload
    return [201, { url: `https://images.unsplash.com/photo-${Date.now()}?w=800&auto=format&fit=crop` }];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/newsletters\/stats$/).reply(200, {
    totalSent: 1540,
    aggregateOpenRate: 68.5,
    aggregateClickThroughRate: 14.2
  });
  mock.onGet(/\/api\/communications\/[^/]+\/contributions$/).reply(200, paginate(mockData.MOCK_CONTRIBUTIONS));
  mock.onGet(/\/api\/communications\/[^/]+\/contributions\/ready$/).reply(200, paginate(mockData.MOCK_CONTRIBUTIONS.filter(c => c.status === 'APPROVED')));
  mock.onPost('/api/communications/contributions').reply((config) => {
    const data = JSON.parse(config.data);
    const newSub = {
      ...data,
      id: `c-${Date.now()}`,
      status: 'PENDING',
      createdAt: new Date().toISOString()
    };
    mockData.MOCK_CONTRIBUTIONS.push(newSub as any);
    return [201, newSub];
  });
  mock.onPost(/\/api\/communications\/contributions\/[^/]+\/approve$/).reply((config) => {
    const segments = config.url?.split('/') || [];
    const id = segments[segments.length - 2]; // .../contributions/{id}/approve
    const sub = mockData.MOCK_CONTRIBUTIONS.find(c => c.id === id);
    if (sub) {
      sub.status = 'APPROVED' as any;
    }
    return [200, sub];
  });
  mock.onPost(/\/api\/communications\/contributions\/[^/]+\/reject$/).reply((config) => {
    const segments = config.url?.split('/') || [];
    const id = segments[segments.length - 2]; 
    const sub = mockData.MOCK_CONTRIBUTIONS.find(c => c.id === id);
    if (sub) {
      sub.status = 'REJECTED' as any;
    }
    return [200, sub];
  });

  // Single Newsletter Studio Mocks
  mock.onGet(/\/api\/communications\/newsletters\/[^/]+$/).reply((config: any) => {
    return [200, mockData.MOCK_NEWSLETTERS[0]];
  });
  mock.onPatch(/\/api\/communications\/newsletters\/[^/]+$/).reply((config: any) => {
    return [200, JSON.parse(config.data)];
  });
  mock.onPost(/\/api\/communications\/newsletters\/[^/]+\/publish$/).reply(200, { success: true });

  // Audit
  mock.onGet(/\/api\/audit\/actions(?:\?.*)?$/).reply(200, ['CREATE_NODE', 'UPDATE_CONFIG', 'PROCESS_TRANSACTION']);
  mock.onGet(/\/api\/audit\/actions\/list(?:\?.*)?$/).reply(200, ['CREATE_NODE', 'UPDATE_CONFIG', 'PROCESS_TRANSACTION']);
  mock.onGet(/\/api\/audit\/[^/]+(?:\?.*)?$/).reply(200, paginate([]));

  // Issues & Support
  // Stateful so created tickets survive a refetch and escalation is observable in dev/mocked runs.
  const mockIssues: any[] = mockData.MOCK_ISSUES.map((i: any) => ({
    ...i,
    originNodeId: i.originNodeId || i.nodeId,
    originNodeName: i.originNodeName,
    escalationTrail: i.escalationTrail || [],
    updates: i.updates || [],
  }));

  const mockNodeName = (nodeId?: string) => {
    if (!nodeId) return undefined;
    if (nodeId === 'platform-root') return 'Platform';
    const match = mockData.NODES.find((n: any) => n.id === nodeId);
    return match ? match.name : nodeId;
  };

  const decorateIssue = (i: any) => ({
    ...i,
    addressedToName: mockNodeName(i.nodeId),
    originNodeName: i.originNodeName || mockNodeName(i.originNodeId),
  });

  const pageOf = (items: any[]) => ({
    content: items,
    totalElements: items.length,
    totalPages: 1,
    size: 50,
    number: 0,
    first: true,
    last: true,
    empty: items.length === 0,
  });

  // Global support queue for system admin portal.
  mock.onGet(/\/api\/admin\/tickets(?:\?.*)?$/i).reply(() => ([200, pageOf(mockIssues.map(decorateIssue))]));

  mock.onDelete(/\/api\/issues\/[^/]+(?:\?.*)?$/).reply((config: any) => {
    const id = config.url?.match(/\/api\/issues\/([^/?]+)/)?.[1];
    const index = mockIssues.findIndex((i: any) => i.id === id);
    if (index !== -1) {
      mockIssues.splice(index, 1);
    }
    return [204];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/issues\/raised(?:\?.*)?$/).reply((config: any) => {
    const nodeId = config.url?.match(/\/api\/nodes\/([^/]+)\//)?.[1];
    const status = config.params?.status;
    const raised = mockIssues.filter(
      (i: any) => i.originNodeId === nodeId && i.nodeId !== nodeId && (!status || i.status === status)
    );
    return [200, pageOf(raised.map(decorateIssue))];
  });

  mock.onGet(/\/api\/nodes\/[^/]+\/issues(?:\?.*)?$/).reply((config: any) => {
    const nodeId = config.url?.match(/\/api\/nodes\/([^/]+)\//)?.[1];
    const { category, severity, status } = config.params || {};
    const filtered = mockIssues.filter(
      (i: any) =>
        i.nodeId === nodeId &&
        (!category || i.category === category) &&
        (!severity || i.severity === severity) &&
        (!status || i.status === status)
    );
    return [200, pageOf(filtered.map(decorateIssue))];
  });

  mock.onGet(/\/api\/issues\/[^/]+(?:\?.*)?$/).reply((config: any) => {
    const id = config.url?.match(/\/api\/issues\/([^/?]+)/)?.[1];
    const ticket = mockIssues.find((i: any) => i.id === id);
    if (!ticket) return [404, { message: 'Ticket not found: ' + id }];
    return [200, decorateIssue(ticket)];
  });

  mock.onPost(/\/api\/nodes\/[^/]+\/issues(?:\?.*)?$/).reply(201, (config: any) => {
    const data = JSON.parse(config.data || '{}');
    const nodeId = config.url?.match(/\/api\/nodes\/([^/]+)\//)?.[1];
    const ticket = {
      id: `issue-${Date.now()}`,
      requesterId: data.requesterId || 'user-james',
      authorName: data.authorName || 'James Avery',
      title: data.title,
      description: data.description,
      category: data.category,
      severity: data.severity,
      status: 'OPEN',
      nodeId,
      originNodeId: nodeId,
      originNodeName: mockNodeName(nodeId),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      escalationTrail: [],
      updates: [],
      escalatedToSystem: false,
    };
    mockIssues.unshift(ticket);
    return [201, decorateIssue(ticket)];
  });

  mock.onPatch(/\/api\/issues\/[^/]+\/status(?:\?.*)?$/).reply(200, (config: any) => {
    const id = config.url?.match(/\/api\/issues\/([^/?]+)/)?.[1];
    const ticket = mockIssues.find((i: any) => i.id === id);
    if (!ticket) return [404, { message: 'Ticket not found: ' + id }];
    const data = JSON.parse(config.data || '{}');
    const previous = ticket.status;
    Object.assign(ticket, data, { updatedAt: new Date().toISOString() });
    if (data.status && data.status !== previous) {
      ticket.updates = [
        ...(ticket.updates || []),
        {
          authorId: 'current-user',
          message: `Status changed from ${previous} to ${data.status}.`,
          timestamp: new Date().toISOString(),
        },
      ];
    }
    return [200, decorateIssue(ticket)];
  });

  mock.onPost(/\/api\/issues\/[^/]+\/escalate(?:\?.*)?$/).reply(200, (config: any) => {
    const id = config.url?.match(/\/api\/issues\/([^/?]+)/)?.[1];
    const ticket = mockIssues.find((i: any) => i.id === id);
    if (!ticket) return [404, { message: 'Ticket not found: ' + id }];
    if (ticket.nodeId === 'platform-root') {
      return [400, { message: 'Ticket is already addressed to the platform root and cannot be escalated further.' }];
    }
    const parent = mockData.NODES.find((n: any) => n.id === ticket.nodeId)?.parentId;
    const target = parent || 'platform-root';
    const hop = {
      fromNodeId: ticket.nodeId,
      fromNodeName: mockNodeName(ticket.nodeId),
      toNodeId: target,
      toNodeName: mockNodeName(target),
      escalatedBy: 'current-user',
      escalatedAt: new Date().toISOString(),
    };
    ticket.escalationTrail = [...(ticket.escalationTrail || []), hop];
    ticket.updates = [
      ...(ticket.updates || []),
      { authorId: 'current-user', message: `Escalated from ${hop.fromNodeName} to ${hop.toNodeName}.`, timestamp: hop.escalatedAt },
    ];
    ticket.nodeId = target;
    ticket.escalatedToSystem = target === 'platform-root';
    ticket.status = 'ESCALATED';
    ticket.updatedAt = new Date().toISOString();
    return [200, decorateIssue(ticket)];
  });

  mock.onPost(/\/api\/issues\/[^/]+\/comments(?:\?.*)?$/).reply(201, (config: any) => {
    const id = config.url?.match(/\/api\/issues\/([^/?]+)/)?.[1];
    const ticket = mockIssues.find((i: any) => i.id === id);
    if (!ticket) return [404, { message: 'Ticket not found: ' + id }];
    const data = JSON.parse(config.data || '{}');
    ticket.updates = [
      ...(ticket.updates || []),
      { authorId: 'current-user', message: data.message, timestamp: new Date().toISOString() },
    ];
    ticket.updatedAt = new Date().toISOString();
    return [201, decorateIssue(ticket)];
  });

  // Events
  mock.onGet(/\/api\/nodes\/[^/]+\/events(?:\?.*)?$/).reply(200, mockData.MOCK_EVENTS);
  mock.onGet(/\/api\/nodes\/[^/]+\/events\/calendar(?:\?.*)?$/).reply(200, mockData.MOCK_EVENTS);
  mock.onPost(/\/api\/nodes\/[^/]+\/events(?:\?.*)?$/).reply(201, (config: any) => [201, JSON.parse(config.data)]);
  mock.onPost(/\/api\/nodes\/[^/]+\/events\/[^/]+\/rsvp(?:\?.*)?$/).reply((config) => {
    const segments = config.url?.split('/') || [];
    const eventId = segments[segments.length - 2];
    const { response } = JSON.parse(config.data);
    
    const event = mockData.MOCK_EVENTS.find(e => e.id === eventId);
    if (event) {
      // Simple mock logic: if changing from NONE/other to YES, increment YES, etc.
      // For simplicity, we'll just update myRsvp and leave counts as is or do a basic shift
      if (event.myRsvp !== response) {
        if (event.myRsvp !== 'NONE') {
          event.rsvpCounts[event.myRsvp] = Math.max(0, event.rsvpCounts[event.myRsvp] - 1);
        }
        if (response !== 'NONE') {
          event.rsvpCounts[response as keyof typeof event.rsvpCounts] = (event.rsvpCounts[response as keyof typeof event.rsvpCounts] || 0) + 1;
        }
        event.myRsvp = response;
      }
    }
    return [200, event];
  });
  mock.onPut(/\/api\/(?:nodes\/[^/]+\/)?events\/[^/]+(?:\?.*)?$/).reply((config: any) => {
    const match = config.url?.match(/\/events\/([^/?]+)/);
    const eventId = match ? match[1] : null;
    const updateData = config.data ? JSON.parse(config.data) : {};
    const event = mockData.MOCK_EVENTS.find(e => e.id === eventId);
    if (event) {
      Object.assign(event, updateData);
      return [200, event];
    }
    return [200, { id: eventId, ...updateData }];
  });
  mock.onPost(/\/api\/(?:nodes\/[^/]+\/)?events\/[^/]+\/cancel(?:\?.*)?$/).reply((config: any) => {
    const match = config.url?.match(/\/events\/([^/?]+)\/cancel/);
    const eventId = match ? match[1] : null;
    const event = mockData.MOCK_EVENTS.find(e => e.id === eventId);
    if (event) {
      event.status = 'CANCELLED';
    }
    return [200, event || { success: true }];
  });

  // Referrals
  mock.onGet(/\/api\/referrals\/history(?:\?.*)?$/).reply(200, paginate([
    {
      id: 'ref1',
      referrerNodeId: 'node1',
      referredNodeName: 'Sons of the American Revolution - Chapter B',
      status: 'ACCEPTED',
      referralCode: 'ORGSETS-123',
      creditsEarned: 5000,
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'ref2',
      referrerNodeId: 'node1',
      referredNodeName: 'Founding Daughters Association',
      status: 'PENDING',
      referralCode: 'ORGSETS-123',
      creditsEarned: 0,
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]));
  mock.onGet(/\/api\/referrals\/me(?:\?.*)?$/).reply(200, { activeCredits: 5000 });

  // Activity & Audit
  const mockNodeActivities = [
    {
      id: 'act-1',
      userId: 'SYSTEM',
      actorName: 'System Daemon',
      nodeId: 'node-rosemont',
      nodeName: 'Rosemont Community Association',
      nodeSlug: 'rosemont',
      action: 'MEMBER_ROLE_CHANGE',
      targetId: 'user_daehli_01',
      targetName: 'Daehli Nadeau',
      metadata: { role: 'ADMIN', name: 'Daehli Nadeau' },
      timestamp: new Date(Date.now() - 3600000 * 2).toISOString(),
      status: 'SUCCESS'
    },
    {
      id: 'act-2',
      userId: 'user_admin_01',
      actorName: 'Richard Admin',
      actorEmail: 'admin@orgsets.com',
      nodeId: 'node-rosemont',
      nodeName: 'Rosemont Community Association',
      nodeSlug: 'rosemont',
      action: 'NODE_CREATED',
      targetId: 'node-rosemont',
      targetName: 'Rosemont Community Association',
      metadata: { title: 'Rosemont', slug: 'rosemont' },
      timestamp: new Date(Date.now() - 3600000 * 5).toISOString(),
      status: 'SUCCESS'
    },
    {
      id: 'act-3',
      userId: 'user_admin_01',
      actorName: 'Richard Admin',
      actorEmail: 'admin@orgsets.com',
      nodeId: 'node-rosemont',
      nodeName: 'Rosemont Community Association',
      nodeSlug: 'rosemont',
      action: 'MODULES_UPDATED',
      targetId: 'node-rosemont',
      targetName: 'Rosemont Community Association',
      metadata: {
        modules: {
          LEDGER: true,
          DIRECTORY: true,
          CALENDAR: true,
          DISCUSSIONS: true,
          PULSE: true,
          SUPPORT: false
        }
      },
      timestamp: new Date(Date.now() - 3600000 * 12).toISOString(),
      status: 'SUCCESS'
    },
    {
      id: 'act-4',
      userId: 'user_daehli_01',
      actorName: 'Daehli Nadeau',
      actorEmail: 'daehli@rosemont.org',
      nodeId: 'node-rosemont',
      nodeName: 'Rosemont Community Association',
      nodeSlug: 'rosemont',
      action: 'MEMBER_JOINED',
      targetId: 'user_daehli_01',
      targetName: 'Daehli Nadeau',
      metadata: { email: 'daehli@rosemont.org', name: 'Daehli Nadeau', role: 'ADMIN' },
      timestamp: new Date(Date.now() - 3600000 * 20).toISOString(),
      status: 'SUCCESS'
    },
    {
      id: 'act-5',
      userId: 'SYSTEM',
      actorName: 'System Daemon',
      nodeId: 'node-rosemont',
      nodeName: 'Rosemont Community Association',
      nodeSlug: 'rosemont',
      action: 'MODULES_UPDATED',
      targetId: 'node-rosemont',
      targetName: 'Rosemont Community Association',
      metadata: {
        modules: {
          SUPPORT: true,
          DIRECTORY: true,
          LEDGER: false,
          PULSE: true,
          NEWSLETTER: true
        }
      },
      timestamp: new Date(Date.now() - 3600000 * 23).toISOString(),
      status: 'SUCCESS'
    }
  ];

  mock.onGet(/\/api\/activity\/nodes\/[^/]+(?:\?.*)?$/).reply(200, paginate(mockNodeActivities));
  mock.onGet(/\/api\/activity\/me(?:\?.*)?$/).reply(200, paginate(mockNodeActivities));

  // Invitations
  mock.onGet(/\/api\/invitations\/validate\/[^/]+(?:\?.*)?$/i).reply((config) => {
    const token = config.url?.split('?')[0].split('/').slice(-1)[0] || 'mock-token';
    return [200, {
      id: 'inv-val-1',
      nodeId: 'node-1',
      nodeName: 'Springfield Parent Teacher Association',
      nodeSlug: 'springfield-pta',
      role: 'MEMBER',
      email: 'invited.member@example.com',
      status: 'PENDING',
      token,
      inviteLink: `https://orgsets.com/join?token=${token}`,
      expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(),
      createdAt: new Date().toISOString()
    }];
  });

  mock.onGet(/\/api\/invitations\/my(?:\?.*)?$/).reply(200, paginate([
    {
      id: 'inv-1',
      nodeId: 'node-1',
      nodeName: 'Springfield Parent Teacher Association',
      role: 'MODERATOR',
      email: 'member@example.com',
      status: 'PENDING',
      token: 'mock-token-1',
      inviteLink: 'https://orgsets.com/join?token=mock-token-1',
      createdAt: new Date(Date.now() - 86400000).toISOString()
    }
  ]));

  mock.onGet(/\/api\/invitations\/[^/]+\/pending(?:\?.*)?$/).reply((config) => {
    const parts = config.url?.split('?')[0].split('/') || [];
    const nodeId = parts[parts.length - 2];
    const node = getCreatedNodes().find(n => n.id === nodeId) || mockData.NODES.find(n => n.id === nodeId);
    return [200, paginate([
      {
        id: 'inv-pending-1',
        nodeId: nodeId || 'node-1',
        nodeName: node?.name || 'Organization',
        role: 'MEMBER',
        email: 'pending.recruit@example.com',
        status: 'PENDING',
        token: 'token-pending-1',
        inviteLink: 'https://orgsets.com/join?token=token-pending-1',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 86400000).toISOString()
      }
    ])];
  });

  mock.onPost(/(?:\/api\/invitations\/accept|\/api\/invitations\/[^/]+\/accept)(?:\?.*)?$/).reply((config) => {
    try {
      const body = JSON.parse(config.data || '{}');
      return [200, { status: 'ACCEPTED', token: body.token || 'mock-token', nodeSlug: 'springfield-pta' }];
    } catch {
      return [200, { status: 'ACCEPTED', nodeSlug: 'springfield-pta' }];
    }
  });
  mock.onPost(/(?:\/api\/invitations\/[^/]+\/revoke|\/api\/invitations\/[^/]+\/decline)(?:\?.*)?$/).reply(200, { success: true });

  // Discovery & Nodes List
  mock.onGet(/\/api\/nodes\/list(?:\?.*)?$/).reply(200, paginate([mockData.MOCK_NODE]));
  mock.onGet(/\/api\/discovery\/suggest(?:\?.*)?$/).reply((config) => {
    const q = (config.params?.query || config.params?.q || '').toLowerCase();
    const suggestions = mockData.NODES.filter(n => 
      n.name.toLowerCase().includes(q) || 
      n.slug.toLowerCase().includes(q)
    );
    return [200, suggestions];
  });
  mock.onGet(/\/api\/discovery\/search(?:\?.*)?$/).reply(200, paginate(mockData.NODES));

  // Health check mock - improved regex to catch absolute and relative paths with query params
  mock.onGet(/.*\/actuator\/health\/(?:readiness|liveness)(?:\?.*)?$/).reply(200, { status: 'UP' });

  // Default pass-through for anything else (if needed)

  // Stripe Subscription Mocks
  mock.onPost(/\/api\/nodes\/[^/]+\/stripe\/subscribe$/).reply((config) => {
    const body = JSON.parse(config.data || '{}');
    const tier = body.tier || 'FOUNDATION';
    return [200, { url: `https://checkout.stripe.com/c/pay/mock_sub_${tier.toLowerCase()}` }];
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/stripe\/portal$/).reply(200, {
    url: 'https://billing.stripe.com/p/session/mock_portal_session'
  });
  mock.onPost(/\/api\/nodes\/[^/]+\/stripe\/cancel$/).reply(200, {
    success: true,
    message: 'Subscription scheduled to cancel at the end of the billing period. Access will be maintained until the period ends.'
  });

  // Error reporting mock — prevents prerender hangs and dev-mode 404s
  mock.onPost(/\/api\/errors\/report(?:\?.*)?$/i).reply(() => {
    return [200, { success: true, ticketId: `ERR-${Date.now()}` }];
  });

  // Data export mocks
  mock.onPost(/\/api\/nodes\/[^/]+\/export(?:\?.*)?$/i).reply(() => {
    return [200, { id: 'exp-mock-1', type: 'NODE', targetId: 'mock-node', status: 'COMPLETED', createdAt: new Date().toISOString() }];
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/export\/status(?:\?.*)?$/i).reply(() => {
    return [200, [{ id: 'exp-mock-1', type: 'NODE', targetId: 'mock-node', status: 'COMPLETED', createdAt: new Date().toISOString() }]];
  });
  mock.onGet(/\/api\/nodes\/[^/]+\/export\/[^/]+\/download(?:\?.*)?$/i).reply(() => {
    return [200, new Blob(['mock zip content'], { type: 'application/zip' })];
  });

  // User account deletion mock
  mock.onDelete(/\/api\/users\/me(?:\?.*)?$/i).reply(() => {
    return [200, { success: true }];
  });

  // Category 3: Content Moderation Queue & Actions
  const mockModerationItems = [
    {
      id: 'mod-1',
      contentType: 'MESSAGE',
      contentId: 'msg-flag-101',
      title: 'Chat Message in #general',
      snippet: 'Please verify the upcoming tournament schedule here: http://unverified-link.biz',
      authorId: 'user-spammer-1',
      authorName: 'Suspicious Member',
      nodeId: 'node-swim-1',
      nodeName: 'PAC Fall 2026 Swim Meet',
      flagReason: 'Automated spam & external link heuristic',
      status: 'PENDING',
      isQuarantined: false,
      createdAt: new Date(Date.now() - 3600000).toISOString()
    },
    {
      id: 'mod-2',
      contentType: 'TOPIC',
      contentId: 'topic-flag-202',
      title: 'Disputed Bylaw Revision Voting Thread',
      snippet: 'Official discussion thread for emergency proxy vote quorum challenges.',
      authorId: 'user-board-2',
      authorName: 'Disputed Member',
      nodeId: 'node-hoa-1',
      nodeName: 'Shady Pines HOA',
      flagReason: 'User report: Harassment & off-topic remarks',
      status: 'QUARANTINED',
      isQuarantined: true,
      createdAt: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: 'mod-3',
      contentType: 'VAULT_ASSET',
      contentId: 'asset-flag-303',
      title: 'Unsigned Liability Waiver Form.pdf',
      snippet: 'https://vault.orgsets.com/assets/waiver_temp.pdf',
      authorId: 'user-coach-3',
      authorName: 'Coach Henderson',
      nodeId: 'node-swim-1',
      nodeName: 'PAC Fall 2026 Swim Meet',
      flagReason: 'Compliance audit: Missing guardian signature block',
      status: 'PENDING',
      isQuarantined: false,
      createdAt: new Date(Date.now() - 172800000).toISOString()
    }
  ];

  mock.onGet(/\/api\/admin\/moderation\/queue(?:\?.*)?$/i).reply((config) => {
    const params = new URLSearchParams(config.url?.split('?')[1] || '');
    const statusFilter = (params.get('status') || 'ALL').toUpperCase();
    const filtered = mockModerationItems.filter(item => {
      if (statusFilter === 'ALL') return true;
      if (statusFilter === 'QUARANTINED') return item.isQuarantined || item.status === 'QUARANTINED';
      if (statusFilter === 'PENDING') return !item.isQuarantined && item.status === 'PENDING';
      return item.status === statusFilter;
    });
    return [200, {
      content: filtered,
      totalElements: filtered.length,
      totalPages: 1,
      size: 10,
      number: 0
    }];
  });

  mock.onPatch(/\/api\/admin\/moderation\/content\/quarantine(?:\?.*)?$/i).reply((config) => {
    try {
      const data = JSON.parse(config.data || '{}');
      const item = mockModerationItems.find(m => m.contentId === data.contentId);
      if (item) {
        item.isQuarantined = Boolean(data.isQuarantined);
        item.status = data.isQuarantined ? 'QUARANTINED' : 'VERIFIED';
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [200, { success: true }];
  });

  mock.onPost(/\/api\/admin\/moderation\/users\/[^/]+\/mute(?:\?.*)?$/i).reply(() => {
    return [200, { success: true }];
  });

  mock.onPost(/\/api\/admin\/moderation\/dismiss(?:\?.*)?$/i).reply((config) => {
    try {
      const data = JSON.parse(config.data || '{}');
      const idx = mockModerationItems.findIndex(m => m.contentId === data.contentId);
      if (idx !== -1) {
        mockModerationItems.splice(idx, 1);
      }
    } catch {
      // Best-effort mock work; the response below is returned regardless.
    }
    return [200, { success: true }];
  });

  // Category 3: Node Lineage & User Telemetry
  mock.onGet(/\/api\/admin\/lineage\/[^/?]+(?:\?.*)?$/i).reply((config) => {
    const match = config.url?.match(/\/api\/admin\/lineage\/([^/?]+)/i);
    const nodeId = match ? match[1] : 'node-1';
    return [200, {
      nodeId,
      nodeName: 'Sample Org Node',
      nodeSlug: 'sample-org-node',
      tier: 'COMMUNITY',
      depth: 1,
      ancestors: [
        { id: 'platform-root', name: 'OrgSets Platform Root', slug: 'global', tier: 'INSTITUTIONAL', depth: 0 }
      ],
      children: [
        { id: 'child-101', name: 'Junior Swim Division', slug: 'junior-swim-division', tier: 'LOCAL', depth: 2 }
      ],
      feeSplitSummary: [
        { nodeName: 'OrgSets Platform Fee', percentage: 2.5, amount: 2.50 },
        { nodeName: 'Regional Governing Council', percentage: 1.5, amount: 1.50 },
        { nodeName: 'Sample Org Node (Net)', percentage: 96.0, amount: 96.00 }
      ]
    }];
  });

  mock.onGet(/\/api\/admin\/telemetry\/users\/[^/]+\/events(?:\?.*)?$/i).reply((config) => {
    const match = config.url?.match(/\/api\/admin\/telemetry\/users\/([^/]+)\/events/i);
    const userId = match ? match[1] : 'user-1';
    const mockEvents = [
      { id: 'ev-1', userId, action: 'SESSION_SIGN_IN', target: 'Clerk Auth', ipAddress: '192.168.1.105', timestamp: new Date(Date.now() - 3600000).toISOString() },
      { id: 'ev-2', userId, action: 'MFA_CHALLENGE_VERIFIED', target: 'SMS OTP Gateway', ipAddress: '192.168.1.105', timestamp: new Date(Date.now() - 3605000).toISOString() },
      { id: 'ev-3', userId, action: 'NODE_MEMBERSHIP_PLACED', target: 'PAC Fall 2026 Swim Meet', ipAddress: '10.0.0.1', timestamp: new Date(Date.now() - 86400000).toISOString() },
      { id: 'ev-4', userId, action: 'LEDGER_PAYMENT_SUBMITTED', target: 'Stripe Intent $120.00', ipAddress: '192.168.1.105', timestamp: new Date(Date.now() - 172800000).toISOString() }
    ];
    return [200, {
      content: mockEvents,
      totalElements: mockEvents.length,
      totalPages: 1,
      size: 20,
      number: 0
    }];
  });

  mock.onGet(/\/api\/system\/logs(?:\?.*)?$/i).reply(() => {
    return [200, [
      `[${new Date().toISOString()}] INFO [SystemMonitor] OrgSets Core JVM healthy. Memory: 142MB/512MB, Active Threads: 18`,
      `[${new Date(Date.now() - 120000).toISOString()}] INFO [SecurityEngine] Rate limiter state active. Zero blacklisted IP anomalies detected in last 5m.`,
      `[${new Date(Date.now() - 300000).toISOString()}] INFO [SyncService] Scheduled Redis/Mongo index reconciliation verified healthy.`
    ]];
  });

  // Category 3: Ledger Reporting Endpoints
  mock.onGet(/\/api\/ledger\/[^/]+\/export(?:\?.*)?$/i).reply(() => {
    const csvContent = "Date,TransactionId,Type,Amount,Currency,Status,CreatedBy,Description\n" +
      `${new Date().toISOString()},tx-001,DUES_PAYMENT,150.00,USD,PAID,user-1,Annual Membership Dues\n` +
      `${new Date(Date.now() - 86400000).toISOString()},tx-002,CONTRIBUTION,50.00,USD,PAID,user-2,Pool Equipment Fundraiser\n` +
      `${new Date(Date.now() - 172800000).toISOString()},tx-003,DUES_PAYMENT,150.00,USD,PENDING,user-3,Fall Meet Registration\n`;
    return [200, new Blob([csvContent], { type: 'text/csv' })];
  });

  mock.onGet(/\/api\/ledger\/[^/]+\/payments(?:\?.*)?$/i).reply(() => {
    return [200, {
      content: [
        { id: 'tx-001', nodeId: 'node-1', amount: 150.00, currency: 'USD', type: 'DUES_PAYMENT', description: 'Annual Membership Dues', status: 'PAID', createdBy: 'user-1', timestamp: new Date().toISOString() },
        { id: 'tx-002', nodeId: 'node-1', amount: 50.00, currency: 'USD', type: 'CONTRIBUTION', description: 'Pool Equipment Fundraiser', status: 'PAID', createdBy: 'user-2', timestamp: new Date(Date.now() - 86400000).toISOString() }
      ],
      totalElements: 2,
      totalPages: 1,
      size: 20,
      number: 0
    }];
  });

  mock.onGet(/\/api\/ledger\/me\/balances(?:\?.*)?$/i).reply(() => {
    return [200, [
      { nodeId: 'node-1', nodeName: 'PAC Fall 2026 Swim Meet', totalPaid: 150.00, totalPending: 0.00, currency: 'USD' },
      { nodeId: 'node-2', nodeName: 'Shady Pines HOA', totalPaid: 320.00, totalPending: 80.00, currency: 'USD' }
    ]];
  });

  // Category 2: Platform GDPR Export & Purge Endpoints
  mock.onPost(/\/api\/admin\/platform\/export(?:\?.*)?$/i).reply((config) => {
    const data = JSON.parse(config.data || '{}');
    const target = data.userId || data.nodeId || 'unknown';
    console.log(`[Mock API] Platform export triggered for ${target}`);
    return [200, { status: 'PENDING', target, message: 'Export job initiated successfully' }];
  });

  mock.onPost(/\/api\/admin\/platform\/purge(?:\?.*)?$/i).reply((config) => {
    const data = JSON.parse(config.data || '{}');
    const userId = data.userId;
    console.log(`[Mock API] Platform purge executed for user ${userId}`);
    return [200, { status: 'SUCCESS', userId, message: 'All user records purged per GDPR compliance' }];
  });

  mock.onAny().passThrough();
}

// Request interceptor — attaches auth + tenant on every call

// Token refresh coordination: a single in-flight refresh promise shared across requests
let tokenRefreshPromise: Promise<string | null> | null = null;
let isBackendReachable = true;

/**
 * Health check management: allow the client to know if the backend is reachable
 * without waiting for individual request timeouts.
 */
export const setBackendReachable = (reachable: boolean) => {
  isBackendReachable = reachable;
};

export const getBackendReachable = () => isBackendReachable;

const reportedErrorCache = new Map<string, number>();

apiWorker.interceptors.request.use(async (config) => {
  console.log(`[API Request Interceptor] Starting request for ${config.url}`);
  
  // Route error telemetry directly to frontend Cloudflare worker so Slack/Mailgun alerts fire
  if (config.url?.startsWith('/api/errors/report')) {
    config.baseURL = '';
    return config;
  }
  
  // Pre-flight availability check: fail fast if we know the backend is down
  // Unless it's a health check or a sync call we're trying to retry
  // Or if we are in mock mode, we always pretend it's reachable to allow mocks to function
  const isActuallyReachable = SHOULD_USE_MOCKS ? true : isBackendReachable;
  
  if (!isActuallyReachable && !config.url?.includes('/actuator/health/readiness')) {
    const error = new Error('OrgSets Backend currently unreachable');
    (error as any).isAvailabilityError = true;
    throw error;
  }

  // Performance tracing
  (config as any).metadata = { startTime: Date.now() };

  // Context resolution: prioritize query param, then active_org from storage.
  // The header is omitted when no org is resolved: everything is path-routed on the
  // platform domain now, so hostname parsing is the wrong oracle (on orgsets.com it
  // yields the literal "orgsets", not a node slug). Let the backend apply its default.
  const searchParams = new URLSearchParams(window.location.search);
  const orgParam = searchParams.get('org');
  const storedOrg = typeof localStorage !== 'undefined' ? localStorage.getItem('active_org') : null;
  const slug = orgParam || storedOrg;

  if (slug) {
    config.headers['X-Org-Tenant'] = slug;
  }

  // Handle Content-Type safely: do NOT force application/json if data is FormData
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  } else if (!config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  // Do not fetch token for actuator health endpoints since they are public
  if (config.url?.includes('/actuator/health/')) {
    return config;
  }

  let token = null;
  
  if (!USE_MOCK_DATA) {
    try {
      if (globalGetToken) {
        token = await globalGetToken();
      } else {
        const Clerk = (window as any).Clerk;
        if (Clerk?.session) {
          try {
            if (!tokenRefreshPromise) {
              tokenRefreshPromise = Clerk.session.getToken().then((newToken: string | null) => {
                tokenRefreshPromise = null;
                return newToken;
              }).catch(() => {
                tokenRefreshPromise = null;
                return null;
              });
            }
            token = await tokenRefreshPromise;
          } catch (e) {
            // Fallback handled
          }
        }
      }
    } catch (e) {
      // Silent fail for token retrieval during init
    }
  }

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

// Response interceptor — unified error handling and performance logging
apiWorker.interceptors.response.use(
  (response) => {
    // If ANY request succeeds, the backend is reachable
    if (!getBackendReachable()) {
      setBackendReachable(true);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('backend:reachable'));
      }
    }

    // Performance monitoring: log slow requests (>1.5s) in development or if they are outliers
    const startTime = (response.config as any).metadata?.startTime;
    if (startTime) {
      const duration = Date.now() - startTime;
      if (duration > 1500) {
        console.warn(`[API Performance] Slow response from ${response.config.url}: ${duration}ms`);
      }
    }
    return response;
  },
  (error) => {
    // Standard status code handling for SPAs
    const status = error.response?.status;
    const url = error.config?.url || '';
    
    // Skip logging for health checks to keep console clean during backend outages
    if (url.includes('/actuator/health/readiness')) {
      return Promise.reject(error);
    }
    
    // Safety check: Avoid redirects during prerendering as it destroys the execution context
    const isPrerendering = (window as any).__PRERENDER_INJECTED === true || 
                          navigator.userAgent.includes('HeadlessChrome') ||
                          window.location.search.includes('__prerender');

    // Global 404 Fallbacks for specific user profile endpoints
    // This safely intercepts 404s and returns empty data for prerendering or mock modes (prevents build timeouts)
    if (status === 404 && (isPrerendering || SHOULD_USE_MOCKS)) {
      // Simple Array Fallbacks
      if (
        url.includes('/api/users/me/announcements') ||
        url.includes('/api/communications/announcements') ||
        url.includes('/api/users/me/preferences') ||
        url.includes('/api/users/me/memberships')
      ) {
        return Promise.resolve({ data: [] });
      }

      // Object Fallbacks
      if (url.includes('/api/users/me/passport')) {
        return Promise.resolve({ data: { vettingLevel: 0, credentials: [] } });
      }
      if (url.includes('/api/ledger/me/balances')) {
        return Promise.resolve({ data: {} });
      }
      if (url.includes('/api/referrals/me')) {
        return Promise.resolve({ data: { activeCredits: 0 } });
      }
      if (url.includes('/api/notifications/unread-count')) {
        return Promise.resolve({ data: { count: 0 } });
      }
      if (url.includes('/api/users/me/dashboard')) {
        return Promise.resolve({ data: { dependentActivity: [], urgentAnnouncements: [], upcomingEvents: [] } });
      }
      if (url.match(/\/api\/nodes\/[^/]+\/dashboard$/)) {
        return Promise.resolve({ data: { recentPosts: [], upcomingEvents: [], activeElections: [], alerts: [] } });
      }
      if (url.includes('/api/invitations/validate/')) {
        return Promise.resolve({
          data: {
            id: 'mock-invite',
            nodeId: 'preview-node',
            nodeName: 'Preview Organization',
            email: 'member@example.com',
            role: 'MEMBER',
            status: 'PENDING'
          }
        });
      }

      // Paginated Fallbacks (Spring Data Page<T> format)
      const paginatedRoutes = [
        '/api/nodes/list', 
        '/api/invitations/my', 
        '/api/invitations/',
        '/pending',
        '/api/activity/me', 
        '/api/ledger/me/sponsorships',
        '/api/notifications',
        '/api/referrals/history'
      ];
      if (paginatedRoutes.some(route => url.includes(route))) {
        return Promise.resolve({
          data: { content: [], totalElements: 0, totalPages: 0, size: 20, number: 0, first: true, last: true, empty: true }
        });
      }

      // Add any additional missing routes here to automatically supply empty states globally
    }

    if (status === 401 && !isPrerendering) {
      // Avoid redirect loops if we are already on sign-in or public pages
      const currentPath = window.location.pathname.toLowerCase();
      const normalizedPath = currentPath.replace(/\/$/, '') || '/';
      
      // Use the centralized public paths list, plus auth-specific paths
      const isPublicPage = PUBLIC_PLATFORM_PATHS.some(route => 
        normalizedPath === route || currentPath.startsWith(route + '/')
      ) || normalizedPath.startsWith('/global') || normalizedPath.startsWith('/unauthorized');

      if (!isPublicPage) {
        console.error("401 Unauthorized encountered on protected route. API:", url);
        // We do NOT dispatch auth:unauthorized here because it causes infinite redirect loops 
        // if the backend and Clerk keys misalign, or if the iframe blocks tokens.
        // Let the React components (via React Query) handle the 401 gracefully.
      }
    } else if (status === 403 && !isPrerendering) {
      console.warn("403 Forbidden encountered. Letting the application handle it.");
      const config = error.config || {};
      const isSilent = Boolean(
        (config as any).silent || 
        (config as any).skipErrorReporting || 
        (config as any).skipToast ||
        config.headers?.['X-Silent-Request']
      );
      const isGameOrLeaderboard = /(?:\/api\/tools\/[^/]+\/high-scores|\/api\/games\/[^/]+\/leaderboard|\/api\/scores)/i.test(url);
      const shouldSuppressGlobal = isSilent || isGameOrLeaderboard;

      const method = (error.config?.method || '').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && !shouldSuppressGlobal) {
        const detail = error.response?.data?.message || error.response?.data?.detail;
        toast.error(detail || "You don't have permission to perform this action.", { id: 'perm-403' });
      }
    } else if (status === 429) {
      toast.error('Too many requests. Please wait a moment before trying again.', { id: 'api-rate-limit' });
    } else if (status && status >= 500) {
      const config = error.config || {};
      const isSilent = Boolean(
        (config as any).silent || 
        (config as any).skipErrorReporting || 
        (config as any).skipToast ||
        config.headers?.['X-Silent-Request']
      );
      const isGameOrLeaderboard = /(?:\/api\/tools\/[^/]+\/high-scores|\/api\/games\/[^/]+\/leaderboard|\/api\/scores)/i.test(url);
      const shouldSuppressGlobal = isSilent || isGameOrLeaderboard;

      if (!shouldSuppressGlobal) {
        toast.error('The server encountered an error. Our team has been notified.', { id: 'api-server-error' });
        if (typeof window !== 'undefined' && !isPrerendering && !url.includes('/api/errors/report')) {
          const method = (error.config?.method || 'REQUEST').toUpperCase();
          const errorKey = `${method}:${url}:${status}`;
          const now = Date.now();
          const lastSent = reportedErrorCache.get(errorKey) || 0;
          if (now - lastSent > 15000) {
            reportedErrorCache.set(errorKey, now);
            apiWorker.post('/api/errors/report', {
              message: `[API ${status}] ${method} ${url}: ${error.response?.data?.message || error.response?.data?.detail || error.message || 'Internal Server Error'}`,
              url: window.location.href,
              timestamp: new Date().toISOString(),
              stack: `Request: ${method} ${url}\nStatus: ${status}\nResponse: ${typeof error.response?.data === 'object' ? JSON.stringify(error.response.data) : String(error.response?.data || '')}\nError: ${error.stack || ''}`,
              componentStack: `Triggered from ${window.location.pathname}`
            }).catch(err => console.warn('Failed to forward error to telemetry endpoint:', err));
          }
        }
      } else {
        console.warn(`[API Server Notice] Suppressed 5xx telemetry for non-critical/silent request: ${error.config?.method || 'GET'} ${url}`);
      }
    } else if (!status && error.message !== 'canceled' && !error.name?.includes('Canceled')) {
      const config = error.config || {};
      const isSilent = Boolean(
        (config as any).silent || 
        (config as any).skipErrorReporting || 
        (config as any).skipToast ||
        config.headers?.['X-Silent-Request']
      );
      const isGameOrLeaderboard = /(?:\/api\/tools\/[^/]+\/high-scores|\/api\/games\/[^/]+\/leaderboard|\/api\/scores)/i.test(url);
      const shouldSuppressGlobal = isSilent || isGameOrLeaderboard;

      if (!shouldSuppressGlobal) {
        if (!isPrerendering) {
          toast.error('Unable to reach server. Please check your connection.', { id: 'api-network-error' });
        }
        if (typeof window !== 'undefined' && !isPrerendering && !url.includes('/api/errors/report')) {
          const method = (error.config?.method || 'REQUEST').toUpperCase();
          const errorKey = `${method}:${url}:NET_ERR`;
          const now = Date.now();
          const lastSent = reportedErrorCache.get(errorKey) || 0;
          if (now - lastSent > 15000) {
            reportedErrorCache.set(errorKey, now);
            apiWorker.post('/api/errors/report', {
              message: `[API NETWORK_FAILURE] ${method} ${url}: ${error.message || 'Connection refused or timeout'}`,
              url: window.location.href,
              timestamp: new Date().toISOString(),
              stack: `Request: ${method} ${url}\nStatus: No Response\nError: ${error.stack || ''}`,
              componentStack: `Triggered from ${window.location.pathname}`
            }).catch(err => console.warn('Failed to forward network error to telemetry endpoint:', err));
          }
        }
      } else {
        console.warn(`[API Network Notice] Suppressed network failure telemetry for non-critical/silent request: ${error.config?.method || 'GET'} ${url}`);
      }
    } else {
      // Log critical errors for observability
      console.error(`[API Error] ${status} at ${url}:`, {
        data: error.response?.data,
        message: error.message,
        headers: error.response?.headers,
        statusText: error.response?.statusText
      });
    }
    
    return Promise.reject(error);
  }
);

export async function getNodeWithFallback(slug: string) {
  try {
    const response = await apiWorker.get(`/api/nodes/by-slug/${slug}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // If it's a 404 or a network error, provide the branded fallback ONLY if mocks are enabled
      if ((error.response?.status === 404 || !error.response) && SHOULD_USE_MOCKS) {
        console.log(`[API Fallback] Providing branded node for ${slug}`);
        return getBrandedNode('orgsets.com', slug);
      }
    }
    throw error;
  }
}

/**
 * Health Check: Proactively verify if the backend is reachable.
 * Tries multiple common endpoints to ensure accuracy across environments.
 */
export async function checkBackendHealth() {
  const timeout = 10000; // 10s timeout
  // Use a relative path to target our Express proxy (avoids CORS issues)
  const endpoint = '/actuator/health/readiness';

  if (ENABLE_HEALTH_CHECKS !== true) {
    if (import.meta.env.DEV) {
      // Optional: console.log('[Health] Check skipped (VITE_ENABLE_HEALTH_CHECKS is not true)');
    }
    return true;
  }

  try {
    console.log(`[Health Check] Verifying backend via proxy: ${endpoint}`);
    // Cache busting ensures we're checking the live backend
    await apiWorker.get(`${endpoint}?health_check=true&_t=${Date.now()}`, { 
      timeout,
      'axios-retry': { retries: 0 } as any,
    });
    console.log('[Health Check] Backend is REACHABLE');
    return true;
  } catch (error) {
    // If we get a response (even 401/403/404), the server is at least UP and responding
    if (axios.isAxiosError(error) && error.response) {
      console.log(`[Health Check] Endpoint ${endpoint} returned status ${error.response.status}. Considering backend UP.`);
      return true;
    }
    console.warn(`[Health Check] Endpoint ${endpoint} failed:`, (error as Error).message);
    return false;
  }
}

/**
 * Just-In-Time Provisioning: Syncs the Clerk User with our MongoDB backend
 */
export async function syncUserWithBackend(userData: any) {
  console.log('[API] syncUserWithBackend called with body:', userData);
  // Use a shorter timeout for identity sync to avoid blocking the UI forever
  try {
    const response = await apiWorker.post('/api/users/me/sync', userData, { 
      timeout: 10000 
    });
    console.log('[API] syncUserWithBackend success:', response.status);
    return response.data;
  } catch (error) {
    console.error('[API] syncUserWithBackend FAILED:', error);
    throw error;
  }
}

/**
 * Stripe Connect: Gets the unique OAuth onboarding link for a node with URL validation
 */
export async function getStripeConnectLink(nodeId: string, options?: { returnUrl?: string; refreshUrl?: string }) {
  // Platform is path-routed under orgsets.com only; satellite brands redirect here
  // for auth/settings flows, so their domains have no business in this allowlist.
  const allowedDomains = ['orgsets.com', 'localhost', '127.0.0.1'];
  
  const validateOrSanitizeUrl = (url?: string, fallback?: string): string => {
    if (!url) return fallback || '';
    if (url.startsWith('/')) return url; // relative path is valid
    try {
      const parsed = new URL(url);
      const isAllowed = allowedDomains.some(d => parsed.hostname === d || parsed.hostname.endsWith(`.${d}`));
      if (isAllowed && (parsed.protocol === 'https:' || parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')) {
        return url;
      }
    } catch {
      // Not a valid URL: fall through to the fallback below.
    }
    return fallback || '';
  };

  const defaultReturnUrl = typeof window !== 'undefined' ? `${window.location.origin}/stripe/callback` : '/stripe/callback';
  const defaultRefreshUrl = typeof window !== 'undefined' ? `${window.location.origin}/settings/payouts?stripe=refresh` : '/settings/payouts';

  const returnUrl = validateOrSanitizeUrl(options?.returnUrl, defaultReturnUrl);
  const refreshUrl = validateOrSanitizeUrl(options?.refreshUrl, defaultRefreshUrl);

  const response = await apiWorker.get(`/api/nodes/${nodeId}/stripe/connect-link`, {
    params: { returnUrl, refreshUrl }
  });
  return response.data;
}

/**
 * Invitations: Accept token-based or ID-based invitation
 */
export async function acceptInvitation(payload: { token?: string; id?: string; firstName?: string; lastName?: string }) {
  if (payload.id && !payload.token) {
    const response = await apiWorker.post(`/api/invitations/${payload.id}/accept`, payload);
    return response.data;
  }
  const response = await apiWorker.post('/api/invitations/accept', payload);
  return response.data;
}

/**
 * Stripe Connect: Verifies the OAuth code returned from Stripe
 */
export async function verifyStripeOAuth(nodeId: string, code: string) {
  const response = await apiWorker.post(`/api/nodes/${nodeId}/stripe/verify-oauth`, {
    code
  });
  return response.data;
}

/**
 * Stripe Subscriptions: Creates a subscription checkout session
 */
export async function createSubscriptionCheckout(nodeId: string, payload: { tier: string; stripePriceId: string; successUrl: string; cancelUrl: string }) {
  const response = await apiWorker.post(`/api/nodes/${nodeId}/stripe/subscribe`, payload);
  return response.data;
}

/**
 * Stripe Subscriptions: Creates a Stripe customer billing portal session
 */
export async function createCustomerPortal(nodeId: string, payload: { returnUrl: string }) {
  const response = await apiWorker.post(`/api/nodes/${nodeId}/stripe/portal`, payload);
  return response.data;
}

/**
 * Stripe Subscriptions: Cancels subscription at period end
 */
export async function cancelSubscription(nodeId: string) {
  const response = await apiWorker.post(`/api/nodes/${nodeId}/stripe/cancel`);
  return response.data;
}

/**
 * Notifications: Get paginated notifications for the bell icon
 */
export async function getNotifications(page = 0, size = 20) {
  const response = await apiWorker.get('/api/notifications', { params: { page, size } });
  return response.data;
}

/**
 * Notifications: Get unread count for the bell badge
 */
export async function getUnreadNotificationCount() {
  const response = await apiWorker.get('/api/notifications/unread-count');
  // Safely extract the count depending on how the backend maps the integer
  return response.data?.count || response.data || 0;
}

/**
 * Notifications: Mark a single notification as read
 */
export async function markNotificationAsRead(id: string) {
  const response = await apiWorker.put(`/api/notifications/${id}/read`);
  return response.data;
}

/**
 * Notifications: Mark all notifications as read
 */
export async function markAllNotificationsAsRead() {
  const response = await apiWorker.put('/api/notifications/read-all');
  return response.data;
}

/**
 * User Profile: Get Announcements with explicit 404 fallback
 */
export async function getMyAnnouncements(page = 0, size = 20) {
  try {
    const response = await apiWorker.get('/api/users/me/announcements', { params: { page, size } });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return [];
    throw error;
  }
}

/**
 * User Profile: Get Preferences with explicit 404 fallback
 */
export async function getMyPreferences() {
  try {
    const response = await apiWorker.get('/api/users/me/preferences');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return [];
    throw error;
  }
}

/**
 * User Profile: Get Passport with explicit 404 fallback
 */
export async function getMyPassport() {
  try {
    const response = await apiWorker.get('/api/users/me/passport');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return { vettingLevel: 0, credentials: [] };
    throw error;
  }
}

/**
 * Ledger & Finance: Process a manual gross contribution
 */
export async function processManualContribution(nodeId: string, data: any) {
  const response = await apiWorker.post(`/api/ledger/${nodeId}/charge`, data);
  return response.data;
}

/**
 * Stripe Connect: Gets the Express Dashboard login link
 */
export async function getStripeDashboardLink(nodeId: string) {
  const response = await apiWorker.get(`/api/nodes/${nodeId}/stripe/dashboard-link`);
  return response.data;
}

/**
 * Newsletter Studio: Get a specific newsletter
 */
export async function getNewsletter(id: string) {
  const response = await apiWorker.get(`/api/newsletters/${id}`);
  return response.data;
}

/**
 * Newsletter Studio: Auto-save or manual save draft updates
 */
export async function updateNewsletter(id: string, data: any) {
  const response = await apiWorker.patch(`/api/newsletters/${id}/metadata`, data);
  return response.data;
}

export async function publishNewsletter(id: string) {
  const response = await apiWorker.post(`/api/newsletters/${id}/publish`);
  return response.data;
}

/**
 * Ledger & Finance: Export full node ledger as CSV
 */
export async function exportNodeLedger(nodeId: string) {
  const response = await apiWorker.get(`/api/ledger/${nodeId}/export`, {
    responseType: 'blob'
  });
  return response.data;
}

/**
 * Pulse & Communications: Acknowledge a Pulse Alert
 */
export async function acknowledgePulseAlert(nodeId: string, alertId: string) {
  const response = await apiWorker.post(`/api/nodes/${nodeId}/pulse/${alertId}/acknowledge`);
  return response.data;
}

/**
 * Newsletter Studio: Subscribe to newsletter
 */
export async function subscribeToNewsletter(newsletterId: string, payload: any) {
  const response = await apiWorker.post(`/api/pulse/${newsletterId}/newsletters/subscribe`, payload);
  return response.data;
}

/**
 * Referrals: Send a referral
 */
export async function sendReferral(nodeId: string, code: string, payload: any) {
  const response = await apiWorker.post(`/api/nodes/${nodeId}/referrals/${code}/send`, payload);
  return response.data;
}

/**
 * Ledger: Buy SMS Credits
 */
export async function buySmsCredits(nodeId: string, payload: any) {
  const quantity = typeof payload === 'number' ? payload : (payload?.quantity || 1);
  const response = await apiWorker.post(`/api/ledger/${nodeId}/buy-sms`, payload, {
    params: { quantity }
  });
  return response.data;
}

/**
 * Channels: Create a new channel
 */
export async function createChannel(payload: any) {
  const response = await apiWorker.post(`/api/channels`, payload, {
    params: payload?.nodeId ? { nodeId: payload.nodeId } : undefined
  });
  return response.data;
}

/**
 * Games: Retrieve leaderboard (paginated)
 */
export async function getGameLeaderboard(gameId: string, limit = 10): Promise<PaginatedResponse<LeaderboardEntry>> {
  const response = await apiWorker.get(`/api/games/${gameId}/leaderboard`, { params: { limit } });
  return response.data;
}

/**
 * Games: Submit a score
 */
export async function submitGameScore(gameId: string, submission: LeaderboardSubmission): Promise<LeaderboardSubmissionResponse> {
  const response = await apiWorker.post(`/api/games/${gameId}/leaderboard`, submission);
  return response.data;
}

/**
 * Admin: Broadcast a notification/announcement blast to a node / platform
 */
export async function sendAdminNodeNotification(nodeId: string, params: AdminBroadcastParams): Promise<Record<string, any>> {
  const response = await apiWorker.post(`/api/admin/nodes/${nodeId}/notifications`, null, { params });
  return response.data;
}

/**
 * Admin: Get active broadcast announcements (paginated)
 */
export async function getAdminSystemAlerts(page = 0, size = 10): Promise<PaginatedResponse<SystemAlert>> {
  const response = await apiWorker.get('/api/admin/notifications/broadcasts', { params: { page, size } });
  return response.data;
}

export const getAdminBroadcasts = getAdminSystemAlerts;

/**
 * Admin: Revoke/delete a broadcast across all users
 */
export async function deleteAdminSystemAlert(alertId: string): Promise<void> {
  await apiWorker.delete(`/api/admin/notifications/broadcasts/${alertId}`);
}

export const deleteAdminBroadcast = deleteAdminSystemAlert;

/**
 * Admin: Get recipients and read receipts for a specific broadcast
 */
export async function getAdminBroadcastRecipients(
  broadcastId: string,
  page = 0,
  size = 10
): Promise<PaginatedResponse<BroadcastRecipient>> {
  const response = await apiWorker.get(`/api/admin/notifications/broadcasts/${broadcastId}/recipients`, {
    params: { page, size }
  });
  return response.data;
}